import { useState, useEffect, useRef, useMemo } from 'react'
import { Form, Button, Row, Col, Alert, Tabs, Tab, Table, InputGroup, Badge, ListGroup, Modal } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { addDaysToDateOnly, getCurrentYearMx, getTodayDateOnlyMx, parseApiDateOnly } from '../../utils/apiDateFormat'
import {
  getIssueDatePickerBoundsMx,
  isIssueDateWithinWindowMx,
  ISSUE_DATE_WINDOW_MESSAGE,
} from '../../utils/issueDateWindow'
import {
  getDisplayDecimals,
  getInputDecimals,
  roundMoneyN,
} from '../../utils/moneyDecimals'
import { rangoImporteRetencion, importeFueraDeRango, mensajeFueraDeRango } from '../../utils/retencionRango'
import { FORMA_PAGO_SAT_OPTIONS, formaPagoDefaultForMetodoPago } from '../../constants/formaPagoSat'
import {
  OBJETO_IMP_SAT_OPTIONS,
  OBJETO_IMP_SIN_DESGLOSE,
  OBJETO_IMP_DEFAULT,
  TIPO_FACTOR_SAT_OPTIONS,
  retencionesPermitidasPfAPm,
  tipoPersonaDesdeRfc,
} from '../../constants/objetoImpSat'

/** RFC genéricos del SAT (Anexo 20). Una factura global va siempre al público en general. */
const RFC_PUBLICO_GENERAL = 'XAXX010101000'
const RAZON_PUBLICO_GENERAL = 'PUBLICO EN GENERAL'

/** Catálogo SAT c_Periodicidad (InformacionGlobal — factura global CFDI 4.0) */
const PERIODICIDAD_SAT = [
  { codigo: '01', descripcion: 'Diario' },
  { codigo: '02', descripcion: 'Semanal' },
  { codigo: '03', descripcion: 'Quincenal' },
  { codigo: '04', descripcion: 'Mensual' },
  { codigo: '05', descripcion: 'Bimestral' }
]

/** Catálogo SAT c_Impuesto (filtrado por tipo en UI) */
const IMPUESTO_CATALOGO = [
  { codigo: '001', etiqueta: 'ISR', traslado: false, retencion: true },
  { codigo: '002', etiqueta: 'IVA', traslado: true, retencion: true },
  { codigo: '003', etiqueta: 'IEPS', traslado: true, retencion: true },
]

const IMPUESTO_LABEL_BY_CODE = IMPUESTO_CATALOGO.reduce((acc, i) => {
  acc[i.codigo] = i.etiqueta
  return acc
}, {})

/** Catálogo SAT c_Meses (InformacionGlobal — factura global CFDI 4.0): 01-12 meses, 13-18 bimestres */
const MESES_SAT = [
  { codigo: '01', descripcion: 'Enero' }, { codigo: '02', descripcion: 'Febrero' },
  { codigo: '03', descripcion: 'Marzo' }, { codigo: '04', descripcion: 'Abril' },
  { codigo: '05', descripcion: 'Mayo' }, { codigo: '06', descripcion: 'Junio' },
  { codigo: '07', descripcion: 'Julio' }, { codigo: '08', descripcion: 'Agosto' },
  { codigo: '09', descripcion: 'Septiembre' }, { codigo: '10', descripcion: 'Octubre' },
  { codigo: '11', descripcion: 'Noviembre' }, { codigo: '12', descripcion: 'Diciembre' },
  { codigo: '13', descripcion: 'Enero-Febrero' }, { codigo: '14', descripcion: 'Marzo-Abril' },
  { codigo: '15', descripcion: 'Mayo-Junio' }, { codigo: '16', descripcion: 'Julio-Agosto' },
  { codigo: '17', descripcion: 'Septiembre-Octubre' }, { codigo: '18', descripcion: 'Noviembre-Diciembre' },
]

function getUsoCfdiEmisor(clients, clientId) {
  if (!clients?.length || clientId == null || clientId === '') return ''
  const clientData = clients.find((c) => c.id === parseInt(clientId, 10))
  return (clientData?.uso_cfdi && String(clientData.uso_cfdi).trim()) || ''
}

function resolveUsoCfdiPorTipoFactura(tipoFactura, emisorUsoCfdi) {
  if (tipoFactura === 'complemento') return 'CP01'
  if (tipoFactura === 'global') return 'S01'
  return emisorUsoCfdi || ''
}

const InvoiceForm = ({ invoice, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    tipo_factura: 'normal',
    client: '',
    configuracion: '',
    invoice_number: '',
    uso_cfdi: '',
    observaciones: '',
    nombre_receptor_pdf: '',
    exportacion: '01',
    moneda: 'MXN',
    tipo_cambio: '1',
    bank_account: '',
    condiciones_pago: '',
    incluir_leyendas_fiscales: false,
    titulo_pdf: '',
    tipo_comprobante_pdf: 'factura',
    desglose_impuestos_pdf: false,
    emisor_regimen_fiscal: '',
    forma_pago: '03',
    metodo_pago: 'PUE',
    complemento_fecha_pago: '',
    complemento_referencia: '',
    issue_date: getTodayDateOnlyMx(),
    due_date: addDaysToDateOnly(getTodayDateOnlyMx(), 30),
    global_periodicidad: '',
    global_meses: '',
    global_anio: getCurrentYearMx(),
    subtotal: 0,
    descuento_total: 0,
    tax_amount: 0,
    total: 0,
    tipo_comprobante: 'I',
    cfdi_calculo_version: 'v2_sat',
    status: 'DRAFT',
    origen: 'manual',
    conceptos: []
  })
  const usaCalculoSatV2 = (formData.cfdi_calculo_version || 'v2_sat') === 'v2_sat'
  const [cfdisRelacionados, setCfdisRelacionados] = useState([]) // [{uuid, tipo_relacion}, ...]
  const [leyendasFiscales, setLeyendasFiscales] = useState([]) // [{disposicionFiscal, norma, textoLeyenda}, ...]
  
  // Datos del receptor (puede ser un cliente del sistema o externo)
  const [receptorClienteId, setReceptorClienteId] = useState('') // ID del cliente receptor si es del sistema
  const [receptorData, setReceptorData] = useState({
    rfc: '',
    razon_social: '',
    direccion: '',
    codigo_postal: '',
    regimen_fiscal: '',
    email: ''
  })
  const [, setReceptorEsExterno] = useState(true) // Por defecto, receptor externo
  const [tipoReceptor, setTipoReceptor] = useState('seleccionado') // 'seleccionado' | 'publico_nacional' | 'publico_extranjero'
  
  // Guardar invoice_number en un estado separado para mejor acceso
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState('')

  const [errors, setErrors] = useState({})
  const [conceptos, setConceptos] = useState([])
  const [cuentasAsignadas, setCuentasAsignadas] = useState({})
  
  // Estados para modal de búsqueda de claves SAT
  const [mostrarModalClaveSAT, setMostrarModalClaveSAT] = useState(false)
  const [conceptoIndexModal, setConceptoIndexModal] = useState(null)
  const [busquedaModalClave, setBusquedaModalClave] = useState('')
  const [resultadosModalClave, setResultadosModalClave] = useState([])
  const [buscandoModalClave, setBuscandoModalClave] = useState(false)
  const [validacionClave, setValidacionClave] = useState({})
  const debounceTimeoutRef = useRef(null)
  /** Valor de clave_unidad al focus (por índice de concepto); para no pisar unidad_medida si el usuario no cambió la clave. */
  const claveUnidadSnapshotRef = useRef({})

  // Estados para modal de búsqueda de cuentas contables
  const [mostrarModalCuenta, setMostrarModalCuenta] = useState(false)
  const [conceptoIndexModalCuenta, setConceptoIndexModalCuenta] = useState(null)
  const [busquedaModalCuenta, setBusquedaModalCuenta] = useState('')
  const [resultadosModalCuenta, setResultadosModalCuenta] = useState([])
  const [buscandoModalCuenta, setBuscandoModalCuenta] = useState(false)
  const debounceTimeoutCuentaRef = useRef(null)
  const [showTaxModal, setShowTaxModal] = useState(false)
  const [taxModalType, setTaxModalType] = useState('traslado') // traslado | retencion
  const [taxConceptIndex, setTaxConceptIndex] = useState(null)
  const [taxDraftRows, setTaxDraftRows] = useState([])
  
  // Estado para la serie seleccionada
  const [serieSeleccionada, setSerieSeleccionada] = useState('')
  const [activeTab, setActiveTab] = useState('general')

  // Modal: conceptos con Clave SAT sin cuenta pero con mapeo disponible
  const [mostrarModalMapeoDisponible, setMostrarModalMapeoDisponible] = useState(false)
  const [conceptosConMapeoDisponible, setConceptosConMapeoDisponible] = useState([])
  const [pendingSubmitData, setPendingSubmitData] = useState(null)

  // Complemento de pago: pago y aplicaciones seleccionadas
  const [complementoPaymentId, setComplementoPaymentId] = useState('')
  const [complementoAplicacionIds, setComplementoAplicacionIds] = useState([])
  /** id aplicación -> número de parcialidad (nuevo complemento y edición borrador) */
  const [complementoParcialidadById, setComplementoParcialidadById] = useState({})

  // Obtener factura completa al editar (asegura uso_cfdi y todos los campos)
  const { data: fullInvoice } = useQuery(
    ['invoice', invoice?.id],
    () => api.get(`/financial/invoices/${invoice.id}/`).then(r => r.data),
    { enabled: !!invoice?.id }
  )
  const invoiceToUse = fullInvoice || invoice
  const esComplemento = (formData.tipo_factura === 'complemento') || (invoiceToUse?.tipo_factura === 'complemento')
  const issueDateBounds = useMemo(() => getIssueDatePickerBoundsMx(), [])

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-invoice',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener configuraciones de facturación del cliente emisor
  const { data: configuraciones } = useQuery(
    ['facturacion-config', formData.client],
    async () => {
      if (!formData.client) return []
      // Solo series activas para emitir
      const response = await api.get(
        `/financial/configuraciones-facturacion/?cliente_id=${formData.client}&activo=true`
      )
      return response.data.results || response.data || []
    },
    { enabled: !!formData.client }
  )
  
  // Obtener configuraciones PAC del cliente emisor
  const { data: configuracionesPAC } = useQuery(
    ['pac-config', formData.client],
    async () => {
      if (!formData.client) return []
      const response = await api.get(`/financial/configuraciones-pac/?cliente_id=${formData.client}&activo=true`)
      return response.data.results || response.data || []
    },
    { enabled: !!formData.client }
  )
  
  // Obtener regímenes fiscales
  const { data: regimenesFiscales } = useQuery(
    'regimenes-fiscales',
    async () => {
      const response = await api.get('/clients/regimenes-fiscales/')
      return response.data.results || response.data || []
    }
  )

  const { data: catalogoMonedas } = useQuery(
    'catalogo-monedas-sat',
    async () => {
      const response = await api.get('/financial/invoices/catalogo-moneda/')
      return response.data || []
    }
  )

  const { data: catalogoPaises } = useQuery(
    'catalogo-pais-sat',
    async () => {
      const response = await api.get('/financial/invoices/catalogo-pais/')
      return response.data || []
    }
  )

  const decimalesInput = useMemo(
    () => getInputDecimals(formData.moneda, catalogoMonedas),
    [catalogoMonedas, formData.moneda]
  )

  const decimalesDisplay = useMemo(
    () => getDisplayDecimals(formData.moneda, catalogoMonedas),
    [catalogoMonedas, formData.moneda]
  )

  const buildDireccionDesdeCliente = (c) => {
    if (!c) return ''
    return [
      c.calle,
      c.numero_exterior ? `#${c.numero_exterior}` : '',
      c.numero_interior ? `Int. ${c.numero_interior}` : '',
      c.colonia,
      c.municipio,
      c.estado_fiscal,
      c.codigo_postal ? `C.P. ${c.codigo_postal}` : '',
      c.pais
    ].filter(Boolean).join(', ') || ''
  }

  const handlePublicoExtranjeroClick = () => {
    if (!formData.client || !clients?.length) {
      toast.error('Seleccione primero el cliente emisor.')
      return
    }
    const emisor = clients.find((x) => x.id === parseInt(formData.client, 10))
    const cpRaw = (emisor?.codigo_postal || '').toString().trim()
    if (!cpRaw || cpRaw.length < 5) {
      toast.error('El emisor debe tener código postal válido (5 caracteres) para usar Público Extranjero.')
      return
    }
    const cp5 = cpRaw.slice(0, 5)
    if (tipoReceptor === 'publico_extranjero') {
      setReceptorData((prev) => ({
        ...prev,
        codigo_postal: cp5,
        direccion: buildDireccionDesdeCliente(emisor)
      }))
      return
    }
    setTipoReceptor('publico_extranjero')
  }

  const handlePublicoNacionalClick = () => {
    if (!formData.client || !clients?.length) {
      toast.error('Seleccione primero el cliente emisor.')
      return
    }
    setTipoReceptor('publico_nacional')
  }
  
  // Obtener configuración seleccionada (después de que configuraciones esté disponible)
  const configuracionSeleccionada = configuraciones?.find(c => c.id === parseInt(formData.configuracion))
  
  // Sincronizar serie seleccionada cuando cambia la configuración
  // Sincronizar serie seleccionada cuando cambia la configuración
  useEffect(() => {
    if (configuracionSeleccionada) {
      const serieDeConfig = configuracionSeleccionada.serie || ''
      setSerieSeleccionada(serieDeConfig)
    }
  }, [configuracionSeleccionada])
  
  // Actualizar serie cuando se cargan las configuraciones y hay un invoice
  useEffect(() => {
    if (invoiceToUse && configuraciones && configuraciones.length > 0) {
      const configId = invoiceToUse.configuracion || invoiceToUse.configuracion_id
      if (configId) {
        const configCargada = configuraciones.find(c => c.id === configId)
        if (configCargada) {
          const serieDeConfig = configCargada.serie || ''
          setSerieSeleccionada(serieDeConfig)
        }
      }
    }
  }, [configuraciones, invoiceToUse])
  
  // Obtener configuración PAC predeterminada del cliente emisor
  const configuracionPACPredeterminada = configuracionesPAC?.find(c => c.es_predeterminado && c.activo) || 
                                          configuracionesPAC?.find(c => c.activo) || null

  // Obtener cuentas bancarias activas del emisor
  const { data: bankAccounts } = useQuery(
    ['bank-accounts-for-invoice', formData.client],
    async () => {
      if (!formData.client) return []
      const response = await api.get('/financial/bank-accounts/', {
        params: { cliente_id: formData.client, is_active: true }
      })
      return response.data.results || response.data || []
    },
    { enabled: !!formData.client }
  )

  // Pagos disponibles para complemento de pago (solo cuando es tipo complemento y hay cliente)
  const { data: pagosParaComplemento } = useQuery(
    ['pagos-para-complemento', formData.client],
    async () => {
      if (!formData.client || formData.tipo_factura !== 'complemento') return []
      const response = await api.get(`/financial/invoices/pagos-para-complemento/`, {
        params: {
          cliente_id: formData.client,
          ...(invoiceToUse?.id && formData.tipo_factura === 'complemento' && !invoiceToUse?.cfdi_uuid ? { invoice_id: invoiceToUse.id } : {})
        }
      })
      return response.data || []
    },
    { enabled: !!formData.client && formData.tipo_factura === 'complemento' }
  )

  // Edición de complemento NO timbrado: incluye aplicaciones ya vinculadas + disponibles del mismo pago
  const {
    data: aplicacionesEditablesComplemento,
    isLoading: loadingAplicacionesEditablesComplemento,
    error: aplicacionesEditablesComplementoError
  } = useQuery(
    ['aplicaciones-editables-complemento', invoiceToUse?.id],
    async () => {
      if (!invoiceToUse?.id) return null
      const response = await api.get(`/financial/invoices/${invoiceToUse.id}/aplicaciones-editables-complemento/`)
      return response.data
    },
    {
      enabled: !!invoiceToUse?.id && invoiceToUse?.tipo_factura === 'complemento' && !invoiceToUse?.cfdi_uuid
    }
  )

  // UX: en nuevo complemento, si hay pagos disponibles y no hay uno seleccionado, seleccionar el primero.
  useEffect(() => {
    if (invoice) return
    if (formData.tipo_factura !== 'complemento') return
    if (complementoPaymentId) return
    if (!Array.isArray(pagosParaComplemento) || pagosParaComplemento.length === 0) return
    const first = pagosParaComplemento[0]?.payment?.id
    if (first != null) setComplementoPaymentId(String(first))
  }, [invoice, formData.tipo_factura, complementoPaymentId, pagosParaComplemento])

  // En edición de complemento no timbrado, inicializar pago seleccionado con el pago actual.
  useEffect(() => {
    if (!invoiceToUse?.id) return
    if (invoiceToUse?.tipo_factura !== 'complemento' || !!invoiceToUse?.cfdi_uuid) return
    if (complementoPaymentId) return
    const p = invoiceToUse?.payment
    if (p && String(p).trim() !== '') {
      setComplementoPaymentId(String(p))
    }
  }, [invoiceToUse?.id, invoiceToUse?.tipo_factura, invoiceToUse?.cfdi_uuid, invoiceToUse?.payment, complementoPaymentId])

  // Auto-fill condiciones_pago cuando cambia cuenta bancaria o fecha de emisión
  const getCondicionesPagoDefault = () => {
    const accId = formData.bank_account
    const acc = Array.isArray(bankAccounts) ? bankAccounts.find(a => a.id === parseInt(accId, 10) || a.id === accId) : null
    if (!acc || !formData.issue_date) return ''
    const d = parseApiDateOnly(formData.issue_date)
    if (!d) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${acc.name || ''} ${acc.account_number || ''} - ${dd} ${mm} ${yyyy}`.trim()
  }

  // Auto-fill condiciones_pago al cambiar cuenta bancaria o fecha. En edición con valor guardado, no sobrescribir en la carga inicial.
  const skipFirstCondicionesUpdate = useRef(!!invoice && !!((invoice?.condiciones_pago || '').trim()))
  useEffect(() => {
    if (formData.bank_account && formData.issue_date && Array.isArray(bankAccounts) && bankAccounts.length > 0) {
      const computed = getCondicionesPagoDefault()
      if (!computed) return
      if (skipFirstCondicionesUpdate.current) {
        skipFirstCondicionesUpdate.current = false
        return
      }
      setFormData(prev => ({ ...prev, condiciones_pago: computed }))
    }
    // getCondicionesPagoDefault se redefine en cada render; incluirla provoca bucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.bank_account, formData.issue_date, bankAccounts])

  useEffect(() => {
    if (!invoice && formData.client && (!formData.bank_account || formData.bank_account === '') && Array.isArray(bankAccounts) && bankAccounts.length > 0) {
      const principales = bankAccounts.filter(acc => acc.es_cuenta_principal === true)
      let selected = null
      if (principales.length > 0) {
        selected = [...principales].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
      }
      if (!selected) {
        selected = bankAccounts[0]
      }
      setFormData(prev => ({ ...prev, bank_account: selected?.id || '' }))
    }
  }, [bankAccounts, formData.client, formData.bank_account, invoice])

  useEffect(() => {
    if (invoiceToUse) {
      // Priorizar uso_cfdi de la factura; fallback al cliente emisor
      const clientData = clients?.find(c => c.id === (invoiceToUse.client || invoiceToUse.client_id))
      const usoCFDI = (invoiceToUse.uso_cfdi && String(invoiceToUse.uso_cfdi).trim()) || (clientData?.uso_cfdi && String(clientData.uso_cfdi).trim()) || ''
      
      // Cargar datos del receptor
      // Helper para filtrar valores "N/A"
      const filterNA = (value) => {
        if (!value || value === 'N/A' || value === 'null' || value === null || value === undefined) {
          return ''
        }
        return String(value).trim()
      }
      
      // El receptor_cliente puede ser un ID o un objeto, dependiendo del serializer
      const receptorClientId = typeof invoiceToUse.receptor_cliente === 'object' ? invoiceToUse.receptor_cliente?.id : invoiceToUse.receptor_cliente || invoiceToUse.receptor_cliente_id
      
      // Primero verificar si hay datos del receptor en el invoice (excluyendo "N/A")
      const receptorRfcValue = filterNA(invoiceToUse.receptor_rfc_display) || filterNA(invoiceToUse.receptor_rfc)
      const receptorNameValue = filterNA(invoiceToUse.receptor_name) || filterNA(invoiceToUse.receptor_razon_social)
      const hasReceptorData = receptorClientId || receptorRfcValue || receptorNameValue
      
      // Detectar Público en General por RFC (antes de cargar datos)
      const rfcVal = (receptorRfcValue || filterNA(invoiceToUse.receptor_rfc) || '').trim().toUpperCase()
      if (rfcVal === 'XAXX010101000') {
        setTipoReceptor('publico_nacional')
      } else if (rfcVal === 'XEXX010101000') {
        setTipoReceptor('publico_extranjero')
      } else {
        setTipoReceptor('seleccionado')
      }

      if (hasReceptorData) {
        if (receptorClientId) {
          // Si el receptor es un cliente del sistema
          const receptorClientIdParsed = parseInt(receptorClientId)
          const receptorClientData = clients?.find(c => c.id === receptorClientIdParsed)
          
          setReceptorClienteId(String(receptorClientId))
          setReceptorEsExterno(false)
          
          // Usar datos del serializer si están disponibles, sino usar datos del cliente
          const regimenFiscalDisplay = invoiceToUse.receptor_regimen_fiscal_display || 
            (receptorClientData?.regimen_fiscal ? {
              codigo: receptorClientData.regimen_fiscal.codigo || receptorClientData.regimen_fiscal?.id?.toString() || '',
              descripcion: receptorClientData.regimen_fiscal.descripcion || receptorClientData.regimen_fiscal?.nombre || ''
            } : null)
          
          const receptorRfcFinal = filterNA(invoiceToUse.receptor_rfc_display) || filterNA(invoiceToUse.receptor_rfc) || receptorClientData?.rfc || ''
          const receptorNameFinal = filterNA(invoiceToUse.receptor_name) || filterNA(invoiceToUse.receptor_razon_social) || 
                                   receptorClientData?.razon_social || receptorClientData?.business_name || 
                                   `${receptorClientData?.nombre || ''} ${receptorClientData?.apellido_paterno || ''} ${receptorClientData?.apellido_materno || ''}`.trim() || ''
          const receptorDireccionFinal = filterNA(invoiceToUse.receptor_direccion_display) || filterNA(invoiceToUse.receptor_direccion) || 
                                        (receptorClientData ? [
                                          receptorClientData.calle,
                                          receptorClientData.numero_exterior ? `#${receptorClientData.numero_exterior}` : '',
                                          receptorClientData.numero_interior ? `Int. ${receptorClientData.numero_interior}` : '',
                                          receptorClientData.colonia,
                                          receptorClientData.municipio,
                                          receptorClientData.estado_fiscal,
                                          receptorClientData.codigo_postal ? `C.P. ${receptorClientData.codigo_postal}` : '',
                                          receptorClientData.pais
                                        ].filter(Boolean).join(', ') : '')
          
          setReceptorData({
            rfc: receptorRfcFinal,
            razon_social: receptorNameFinal,
            direccion: receptorDireccionFinal,
            codigo_postal: filterNA(invoiceToUse.receptor_codigo_postal) || receptorClientData?.codigo_postal || '',
            regimen_fiscal: regimenFiscalDisplay ? 
                           `${regimenFiscalDisplay.codigo} - ${regimenFiscalDisplay.descripcion}` : '',
            email: filterNA(invoiceToUse.receptor_email) || receptorClientData?.email || '',
            residencia_fiscal: '',
            num_reg_id_trib: ''
          })
        } else {
          // Si el receptor es externo (datos manuales). Público en general: siempre "PUBLICO EN GENERAL"
          setReceptorClienteId('')
          setReceptorEsExterno(true)
          const regimenFiscalDisplay = invoiceToUse.receptor_regimen_fiscal_display
          const razonSocialExterno = (rfcVal === 'XAXX010101000' || rfcVal === 'XEXX010101000')
            ? 'PUBLICO EN GENERAL' : receptorNameValue
          setReceptorData({
            rfc: receptorRfcValue,
            razon_social: razonSocialExterno,
            direccion: filterNA(invoiceToUse.receptor_direccion_display) || filterNA(invoiceToUse.receptor_direccion) || '',
            codigo_postal: filterNA(invoiceToUse.receptor_codigo_postal) || '',
            regimen_fiscal: regimenFiscalDisplay ? 
                           `${regimenFiscalDisplay.codigo} - ${regimenFiscalDisplay.descripcion}` :
                           (filterNA(invoiceToUse.receptor_regimen_fiscal) || ''),
            email: filterNA(invoiceToUse.receptor_email) || '',
            residencia_fiscal: filterNA(invoiceToUse.receptor_residencia_fiscal) || '',
            num_reg_id_trib: filterNA(invoiceToUse.receptor_num_reg_id_trib) || ''
          })
        }
      } else {
        // Si no hay datos del receptor, limpiar
        setReceptorClienteId('')
        setReceptorEsExterno(true)
        setReceptorData({
          rfc: '',
          razon_social: '',
          direccion: '',
          codigo_postal: '',
          regimen_fiscal: '',
          email: '',
          residencia_fiscal: '',
          num_reg_id_trib: ''
        })
      }
      
      // Cargar serie desde la configuración o desde el invoice_number
      let serieCargada = ''
      if (invoiceToUse.configuracion_serie) {
        serieCargada = invoiceToUse.configuracion_serie
      } else if (invoiceToUse.invoice_number) {
        // Extraer serie del invoice_number (ej: "FAC-000001" -> "FAC")
        const serieMatch = invoiceToUse.invoice_number.match(/^([A-Za-z0-9]+)-/)
        if (serieMatch) {
          serieCargada = serieMatch[1]
        }
      }
      
      const clientEmisor = clients?.find(c => c.id === (invoiceToUse.client || invoiceToUse.client_id))
      const emisorRegimenId = invoiceToUse.emisor_regimen_fiscal ?? invoiceToUse.emisor_regimen_fiscal_id ?? (clientEmisor?.regimen_fiscal?.id ?? clientEmisor?.regimen_fiscal ?? '')

      setFormData({
        tipo_factura: invoiceToUse.tipo_factura || 'normal',
        client: invoiceToUse.client || invoiceToUse.client_id || '',
        configuracion: invoiceToUse.configuracion || invoiceToUse.configuracion_id || '',
        invoice_number: invoiceToUse.invoice_number || '', // Guardar el número de factura original
        uso_cfdi: usoCFDI,
        tipo_comprobante: (invoiceToUse.tipo_comprobante || 'I').toUpperCase() === 'E' ? 'E' : 'I',
        cfdi_calculo_version: invoiceToUse.cfdi_calculo_version || 'v1_neto',
        descuento_total: invoiceToUse.descuento_total || 0,
        observaciones: invoiceToUse.observaciones || '',
        nombre_receptor_pdf: invoiceToUse.nombre_receptor_pdf || '',
        exportacion: invoiceToUse.exportacion || '01',
        moneda: invoiceToUse.moneda || 'MXN',
        tipo_cambio: String(invoiceToUse.tipo_cambio ?? '1'),
        bank_account: invoiceToUse.bank_account || invoiceToUse.bank_account_id || '',
        condiciones_pago: invoiceToUse.condiciones_pago || '',
        // Manda lo que la factura trae; el nombre comercial del cliente es solo el valor
        // inicial cuando la factura no tiene ninguno. Al reves, clonar una factura que
        // decia "Recibo de honorarios" la devolvia a la etiqueta del cliente y el campo se
        // sentia congelado: se escribia y volvia a cambiar solo. Lo reporto el contador el
        // 01/09/2026 (punto 10).
        titulo_pdf: (invoiceToUse.titulo_pdf || clientEmisor?.nombre_comercial_pdf || ''),
        tipo_comprobante_pdf: invoiceToUse.tipo_comprobante_pdf || 'factura',
        desglose_impuestos_pdf: !!invoiceToUse.desglose_impuestos_pdf,
        emisor_regimen_fiscal: emisorRegimenId || '',
        forma_pago: (invoiceToUse.metodo_pago || 'PUE') === 'PPD'
          ? '99'
          : (invoiceToUse.forma_pago || '03'),
        metodo_pago: invoiceToUse.metodo_pago || 'PUE',
        complemento_fecha_pago: invoiceToUse.complemento_fecha_pago || '',
        complemento_referencia: invoiceToUse.complemento_referencia || '',
        incluir_leyendas_fiscales: invoiceToUse.incluir_leyendas_fiscales === true,
        issue_date: invoiceToUse.issue_date || getTodayDateOnlyMx(),
        due_date: invoiceToUse.due_date || addDaysToDateOnly(getTodayDateOnlyMx(), 30),
        global_periodicidad: invoiceToUse.global_periodicidad || '',
        global_meses: invoiceToUse.global_meses || '',
        global_anio: invoiceToUse.global_anio ?? getCurrentYearMx(),
        subtotal: invoiceToUse.subtotal || 0,
        tax_amount: invoiceToUse.tax_amount || 0,
        total: invoiceToUse.total || 0,
        status: invoiceToUse.status || 'DRAFT',
        origen: invoiceToUse.origen || 'manual',
        conceptos: invoiceToUse.conceptos || []
      })

      setCfdisRelacionados(Array.isArray(invoiceToUse.cfdis_relacionados) ? invoiceToUse.cfdis_relacionados.map(r => ({
        uuid: r.uuid || '',
        tipo_relacion: r.tipo_relacion || '01'
      })) : [])
      setLeyendasFiscales(Array.isArray(invoiceToUse.leyendas_fiscales) ? invoiceToUse.leyendas_fiscales.map(l => ({
        disposicionFiscal: l.disposicionFiscal || '',
        norma: l.norma || '',
        textoLeyenda: l.textoLeyenda || ''
      })) : [])
      
      // Si hay configuración, actualizar la serie desde la configuración
      const configId = invoice.configuracion || invoice.configuracion_id
      if (configId) {
        // Intentar actualizar serie cuando configuraciones esté disponible
        if (configuraciones && configuraciones.length > 0) {
          const configCargada = configuraciones.find(c => c.id === configId)
          if (configCargada) {
            // Si la configuración tiene serie, usarla; sino, es "Sin serie"
            if (configCargada.serie) {
              setSerieSeleccionada(configCargada.serie)
            } else {
              setSerieSeleccionada('') // Sin serie
            }
          } else if (serieCargada) {
            // Si no se encuentra la configuración pero hay serieCargada, usarla
            setSerieSeleccionada(serieCargada)
          }
        } else if (serieCargada) {
          // Si no hay configuraciones cargadas aún, usar serieCargada temporalmente
          setSerieSeleccionada(serieCargada)
        } else if (invoice.configuracion_serie) {
          // Si tenemos configuracion_serie del serializer, usarla
          setSerieSeleccionada(invoice.configuracion_serie)
        }
      } else if (serieCargada) {
        setSerieSeleccionada(serieCargada)
      } else if (invoice.configuracion_serie) {
        // Si tenemos configuracion_serie del serializer, usarla
        setSerieSeleccionada(invoice.configuracion_serie)
      } else {
        // Si no hay configuración ni serie, mantener vacío
        setSerieSeleccionada('')
      }
      
      // Guardar invoice_number en estado separado para mejor acceso
      setCurrentInvoiceNumber(invoiceToUse.invoice_number || '')
      const conceptosNorm = (invoiceToUse.conceptos || []).map((c) => {
        const ir = parseFloat(c.iva_retenido) || 0
        const tras = Array.isArray(c.impuestos_trasladados) ? c.impuestos_trasladados : []
        const rets = Array.isArray(c.retenciones_detalle) ? c.retenciones_detalle : []
        const conceptWithDetail = {
          ...c,
          impuestos_trasladados: tras,
          retenciones_detalle: rets,
        }
        const recalculated = recalculateConceptTaxDetails(conceptWithDetail, decimalesInput)
        const ivaTras = roundMoneyN(
          (recalculated.impuestos_trasladados || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
          decimalesInput
        )
        const ivaRet = roundMoneyN(
          (recalculated.retenciones_detalle || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
          decimalesInput
        )
        const trasNorm = (recalculated.impuestos_trasladados || []).map((r) => ({
          ...r,
          tipo_factor: r.tipo_factor || 'Tasa',
        }))
        const retNorm = (recalculated.retenciones_detalle || []).map((r) => ({
          ...r,
          tipo_factor: r.tipo_factor || 'Tasa',
        }))
        return {
          ...recalculated,
          objeto_imp: (c.objeto_imp || '').trim() || (
            trasNorm.length || retNorm.length ? OBJETO_IMP_DEFAULT : '01'
          ),
          impuestos_trasladados: trasNorm,
          retenciones_detalle: retNorm,
          iva_trasladado: ivaTras || (parseFloat(c.iva_trasladado) || 0),
          iva_retenido: ivaRet || (parseFloat(c.iva_retenido) || 0),
          retencion_impuesto: (c.retencion_impuesto || '').trim() || (ir > 0 ? '002' : '') || (recalculated.retenciones_detalle?.[0]?.impuesto || ''),
          cuentas_predial: Array.isArray(c.cuentas_predial)
            ? c.cuentas_predial.map((x) => String(x || '').trim()).filter(Boolean)
            : [],
        }
      })
      setConceptos(conceptosNorm)
      calculateTotals(conceptosNorm)
      
      // Obtener cuentas asignadas para conceptos existentes
      const nuevasCuentasAsignadas = {}
      if (invoiceToUse.conceptos && invoiceToUse.conceptos.length > 0) {
        invoiceToUse.conceptos.forEach((concepto, index) => {
          if (concepto.cuenta_contable_code && concepto.cuenta_contable_asignada) {
            nuevasCuentasAsignadas[index] = {
              id: concepto.cuenta_contable_asignada,
              code: concepto.cuenta_contable_code,
              name: concepto.cuenta_contable_name || concepto.cuenta_contable_code
            }
            // También guardar con string para compatibilidad
            nuevasCuentasAsignadas[String(index)] = nuevasCuentasAsignadas[index]
          }
        })
      }
      
      // Actualizar todas las cuentas asignadas de una vez
      if (Object.keys(nuevasCuentasAsignadas).length > 0) {
        setCuentasAsignadas(nuevasCuentasAsignadas)
      }
      
      // Si hay conceptos sin cuenta asignada, intentar obtenerlas después
      if (invoiceToUse.conceptos && invoiceToUse.client) {
        invoiceToUse.conceptos.forEach((concepto, index) => {
          if (!nuevasCuentasAsignadas[index] && (concepto.clave_producto_servicio || concepto.descripcion)) {
            setTimeout(() => obtenerCuentaAsignada(index), 500 * (index + 1))
          }
        })
      }
    } else {
      // Si no hay invoice (nueva factura), limpiar
      setCurrentInvoiceNumber('')
      setSerieSeleccionada('')
      setCfdisRelacionados([])
      setLeyendasFiscales([])
      setReceptorClienteId('')
      setReceptorEsExterno(true)
      setTipoReceptor('seleccionado')
      setReceptorData({
        rfc: '',
        razon_social: '',
        direccion: '',
        codigo_postal: '',
        regimen_fiscal: '',
        email: '',
        residencia_fiscal: '',
        num_reg_id_trib: ''
      })
    }
    // calculateTotals y las demas se redefinen en cada render. Este efecto solo
    // debe correr al cargar/cambiar la factura, no en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceToUse, clients, configuraciones])

  // Inicializar selección/parcialidades al cargar complemento en edición
  useEffect(() => {
    if (invoiceToUse?.tipo_factura !== 'complemento' || !!invoiceToUse?.cfdi_uuid) return
    const rows = aplicacionesEditablesComplemento?.rows
    if (Array.isArray(rows) && rows.length > 0) {
      const selected = []
      const m = {}
      rows.forEach((a) => {
        if (a.selected) selected.push(a.id)
        const base = a.num_parcialidad != null ? Number(a.num_parcialidad) : Number(a.num_parcialidad_sugerida ?? 1)
        m[a.id] = Number.isFinite(base) && base >= 1 ? base : 1
      })
      setComplementoAplicacionIds(selected)
      setComplementoParcialidadById(m)
      return
    }
    const ads = invoiceToUse?.aplicaciones_documentadas
    if (Array.isArray(ads) && ads.length > 0) {
      const selected = []
      const m = {}
      ads.forEach((a) => {
        selected.push(a.id)
        m[a.id] = a.num_parcialidad != null ? Number(a.num_parcialidad) : 1
      })
      setComplementoAplicacionIds(selected)
      setComplementoParcialidadById(m)
    }
  }, [invoiceToUse?.id, invoiceToUse?.tipo_factura, invoiceToUse?.cfdi_uuid, invoiceToUse?.aplicaciones_documentadas, aplicacionesEditablesComplemento?.rows])
  
  // Actualizar uso_cfdi y emisor_regimen_fiscal cuando cambia el cliente (para nuevas facturas)
  useEffect(() => {
    if (!invoice && formData.client && clients?.length) {
      const clientData = clients.find(c => c.id === parseInt(formData.client))
      if (!clientData) return
      const updates = {}
      if (!formData.uso_cfdi) {
        const emisorUso = getUsoCfdiEmisor(clients, formData.client)
        const usoResuelto = resolveUsoCfdiPorTipoFactura(formData.tipo_factura, emisorUso)
        if (usoResuelto) updates.uso_cfdi = usoResuelto
      }
      // Régimen fiscal emisor: API devuelve regimen_fiscal como ID o como objeto
      if (!formData.emisor_regimen_fiscal && clientData.regimen_fiscal != null) {
        const r = clientData.regimen_fiscal
        const emisorRegimenId = (typeof r === 'object' && r?.id != null)
          ? r.id
          : (typeof r === 'number' || (typeof r === 'string' && String(r).trim()))
            ? r
            : ''
        if (emisorRegimenId) updates.emisor_regimen_fiscal = emisorRegimenId
      }
      if (Object.keys(updates).length) {
        setFormData(prev => ({ ...prev, ...updates }))
      }
    }
  }, [formData.client, formData.uso_cfdi, formData.emisor_regimen_fiscal, formData.tipo_factura, clients, invoice])
  
  // Recargar datos del receptor cuando se cargan los clients (para asegurar que se muestren en edición)
  useEffect(() => {
    if (invoiceToUse && clients && clients.length > 0) {
      // Helper para filtrar valores "N/A"
      const filterNA = (value) => {
        if (!value || value === 'N/A' || value === 'null' || value === null || value === undefined) {
          return ''
        }
        return String(value).trim()
      }
      
      // Solo recargar si no hay datos del receptor cargados aún o si solo hay valores "N/A"
      const hasValidReceptorData = receptorData.rfc && receptorData.rfc !== 'N/A' && receptorData.rfc.trim() !== '' ||
                                   receptorData.razon_social && receptorData.razon_social !== 'N/A' && receptorData.razon_social.trim() !== '' ||
                                   receptorClienteId
      
      if (!hasValidReceptorData) {
        // Intentar cargar los datos del receptor nuevamente
        const receptorClientId = typeof invoiceToUse.receptor_cliente === 'object' ? invoiceToUse.receptor_cliente?.id : invoiceToUse.receptor_cliente || invoiceToUse.receptor_cliente_id
        
        if (receptorClientId) {
          const receptorClientIdParsed = parseInt(receptorClientId)
          const receptorClientData = clients.find(c => c.id === receptorClientIdParsed)
          
          if (receptorClientData) {
            setReceptorClienteId(String(receptorClientId))
            setReceptorEsExterno(false)
            
            const regimenFiscalDisplay = invoiceToUse.receptor_regimen_fiscal_display || 
              (receptorClientData?.regimen_fiscal ? {
                codigo: receptorClientData.regimen_fiscal.codigo || receptorClientData.regimen_fiscal?.id?.toString() || '',
                descripcion: receptorClientData.regimen_fiscal.descripcion || receptorClientData.regimen_fiscal?.nombre || ''
              } : null)
            
            const receptorRfcFinal = filterNA(invoiceToUse.receptor_rfc_display) || filterNA(invoiceToUse.receptor_rfc) || receptorClientData.rfc || ''
            const receptorNameFinal = filterNA(invoiceToUse.receptor_name) || filterNA(invoiceToUse.receptor_razon_social) || 
                                     receptorClientData.razon_social || receptorClientData.business_name || 
                                     `${receptorClientData.nombre || ''} ${receptorClientData.apellido_paterno || ''} ${receptorClientData.apellido_materno || ''}`.trim() || ''
            const receptorDireccionFinal = filterNA(invoiceToUse.receptor_direccion_display) || filterNA(invoiceToUse.receptor_direccion) || [
              receptorClientData.calle,
              receptorClientData.numero_exterior ? `#${receptorClientData.numero_exterior}` : '',
              receptorClientData.numero_interior ? `Int. ${receptorClientData.numero_interior}` : '',
              receptorClientData.colonia,
              receptorClientData.municipio,
              receptorClientData.estado_fiscal,
              receptorClientData.codigo_postal ? `C.P. ${receptorClientData.codigo_postal}` : '',
              receptorClientData.pais
            ].filter(Boolean).join(', ') || ''
            
            setReceptorData({
              rfc: receptorRfcFinal,
              razon_social: receptorNameFinal,
              direccion: receptorDireccionFinal,
              codigo_postal: filterNA(invoiceToUse.receptor_codigo_postal) || receptorClientData.codigo_postal || '',
              regimen_fiscal: regimenFiscalDisplay ? 
                             `${regimenFiscalDisplay.codigo} - ${regimenFiscalDisplay.descripcion}` : '',
              email: filterNA(invoiceToUse.receptor_email) || receptorClientData.email || '',
              residencia_fiscal: '',
              num_reg_id_trib: ''
            })
          }
        } else {
          const receptorRfcValue = filterNA(invoiceToUse.receptor_rfc_display) || filterNA(invoiceToUse.receptor_rfc)
          const receptorNameValue = filterNA(invoiceToUse.receptor_name) || filterNA(invoiceToUse.receptor_razon_social)
          
          if (receptorRfcValue || receptorNameValue) {
            setReceptorClienteId('')
            setReceptorEsExterno(true)
            const regimenFiscalDisplay = invoiceToUse.receptor_regimen_fiscal_display
            setReceptorData({
              rfc: receptorRfcValue,
              razon_social: receptorNameValue,
              direccion: filterNA(invoiceToUse.receptor_direccion_display) || filterNA(invoiceToUse.receptor_direccion) || '',
              codigo_postal: filterNA(invoiceToUse.receptor_codigo_postal) || '',
              regimen_fiscal: regimenFiscalDisplay ? 
                             `${regimenFiscalDisplay.codigo} - ${regimenFiscalDisplay.descripcion}` :
                             (filterNA(invoiceToUse.receptor_regimen_fiscal) || ''),
              email: filterNA(invoiceToUse.receptor_email) || '',
              residencia_fiscal: filterNA(invoiceToUse.receptor_residencia_fiscal) || '',
              num_reg_id_trib: filterNA(invoiceToUse.receptor_num_reg_id_trib) || ''
            })
          }
        }
      }
    }
  }, [clients, invoiceToUse, receptorData.rfc, receptorData.razon_social, receptorClienteId])

  // Llenar receptor desde emisor cuando es Público en General
  useEffect(() => {
    if ((tipoReceptor === 'publico_nacional' || tipoReceptor === 'publico_extranjero') && formData.client && clients) {
      const emisorClient = clients.find(c => c.id === parseInt(formData.client))
      if (emisorClient) {
        // Público en General usa siempre régimen 616 - Sin obligaciones fiscales
        const regimen616 = regimenesFiscales?.find(r => r.codigo === '616') || { codigo: '616', descripcion: 'Sin obligaciones fiscales' }
        const regimenStr = `${regimen616.codigo} - ${regimen616.descripcion}`
        const direccionEmisor = buildDireccionDesdeCliente(emisorClient)
        const cpEmisor = (emisorClient.codigo_postal || '').toString().trim().slice(0, 5)
        const esExtranjero = tipoReceptor === 'publico_extranjero'
        const emailEmisor = emisorClient.email || ''
        setReceptorClienteId('')
        setReceptorEsExterno(true)
        setReceptorData((prev) => ({
          rfc: esExtranjero ? 'XEXX010101000' : 'XAXX010101000',
          razon_social: esExtranjero ? 'PUBLICO EXTRANJERO' : 'PUBLICO EN GENERAL',
          // Extranjero: solo CP del emisor (= DomicilioFiscalReceptor). No copiar dirección ni email del emisor.
          direccion: esExtranjero
            ? ((prev.direccion || '') === direccionEmisor ? '' : (prev.direccion || ''))
            : direccionEmisor,
          codigo_postal: esExtranjero ? cpEmisor : (emisorClient.codigo_postal || ''),
          regimen_fiscal: regimenStr,
          email: esExtranjero
            ? ((prev.email || '') === emailEmisor ? '' : (prev.email || ''))
            : emailEmisor,
          ...(esExtranjero
            ? {
                residencia_fiscal: (prev.residencia_fiscal || '').trim().toUpperCase().slice(0, 3),
                num_reg_id_trib: (prev.num_reg_id_trib || '').trim().slice(0, 40)
              }
            : { residencia_fiscal: '', num_reg_id_trib: '' })
        }))
        setFormData(prev => ({ ...prev, uso_cfdi: 'S01' }))
      }
    }
  }, [tipoReceptor, formData.client, clients, regimenesFiscales])

  // Público extranjero: Razón Social automática = "PUBLICO EXTRANJERO" + Nombre Receptor.
  useEffect(() => {
    if (tipoReceptor !== 'publico_extranjero') return
    const nombre = (formData.nombre_receptor_pdf || '').trim()
    const razonAuto = nombre ? `PUBLICO EXTRANJERO ${nombre}` : 'PUBLICO EXTRANJERO'
    setReceptorData((prev) => (prev.razon_social === razonAuto ? prev : { ...prev, razon_social: razonAuto }))
  }, [tipoReceptor, formData.nombre_receptor_pdf])

  // Factura Global: el receptor es el PUBLICO EN GENERAL, no el emisor.
  //
  // Antes copiaba aqui los datos del emisor --RFC y razon social incluidos-- y los volvia a
  // copiar cada vez que el efecto se re-disparaba, asi que pisaba lo que el usuario acababa
  // de elegir al cambiar de pestana. El contador lo reporto el 01/09/2026: *"al elegir global
  // me pone en automatico los datos del emisor en la pestana de receptor... al cambiar a
  // conceptos me los cambia de nuevo"*.
  //
  // No era cosmetico: **3 de 34 globales se timbraron a nombre del propio contribuyente**,
  // $47,401.65 ante el SAT. Del emisor se toma solo lo que el Anexo 20 manda tomar: el
  // domicilio fiscal del receptor es el lugar de expedicion.
  useEffect(() => {
    if (formData.tipo_factura === 'global' && formData.client && clients) {
      const emisorClient = clients.find(c => c.id === parseInt(formData.client))
      if (emisorClient) {
        const regimen616 = regimenesFiscales?.find(r => r.codigo === '616') || { codigo: '616', descripcion: 'Sin obligaciones fiscales' }
        const regimenStr = `${regimen616.codigo} - ${regimen616.descripcion}`
        const direccionEmisor = [
          emisorClient.calle,
          emisorClient.numero_exterior ? `#${emisorClient.numero_exterior}` : '',
          emisorClient.numero_interior ? `Int. ${emisorClient.numero_interior}` : '',
          emisorClient.colonia,
          emisorClient.municipio,
          emisorClient.estado_fiscal,
          emisorClient.codigo_postal ? `C.P. ${emisorClient.codigo_postal}` : '',
          emisorClient.pais
        ].filter(Boolean).join(', ') || ''
        setReceptorClienteId('')
        setReceptorEsExterno(true)
        setReceptorData({
          rfc: RFC_PUBLICO_GENERAL,
          razon_social: RAZON_PUBLICO_GENERAL,
          // Del emisor solo el domicilio: en una global, DomicilioFiscalReceptor es el lugar
          // de expedicion del emisor.
          direccion: direccionEmisor,
          codigo_postal: emisorClient.codigo_postal || '',
          regimen_fiscal: regimenStr,
          email: emisorClient.email || '',
          residencia_fiscal: '',
          num_reg_id_trib: ''
        })
        setFormData((prev) => (prev.uso_cfdi === 'S01' ? prev : { ...prev, uso_cfdi: 'S01' }))
      }
    }
  }, [formData.tipo_factura, formData.client, clients, regimenesFiscales])

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Si cambia tipo de factura
    if (name === 'tipo_factura') {
      const emisorUso = getUsoCfdiEmisor(clients, formData.client)
      setFormData(prev => {
        const saliendoDeTipoEspecial = prev.tipo_factura === 'complemento' || prev.tipo_factura === 'global'
        const usoCfdi = value === 'complemento' || value === 'global'
          ? resolveUsoCfdiPorTipoFactura(value, emisorUso)
          : (saliendoDeTipoEspecial ? (emisorUso || prev.uso_cfdi) : prev.uso_cfdi)
        return {
          ...prev,
          [name]: value,
          uso_cfdi: usoCfdi,
          ...(value === 'complemento' ? { tipo_comprobante_pdf: 'complemento_pago' } : {}),
          global_periodicidad: value === 'global' ? prev.global_periodicidad : '',
          global_meses: value === 'global' ? prev.global_meses : '',
          global_anio: value === 'global' ? (prev.global_anio ?? getCurrentYearMx()) : null
        }
      })
      if (value !== 'complemento') {
        setComplementoPaymentId('')
        setComplementoAplicacionIds([])
      }
      return
    }

    if (name === 'moneda') {
      const nextMoneda = (value || 'MXN').toUpperCase()
      setFormData(prev => ({
        ...prev,
        moneda: nextMoneda,
        tipo_cambio: nextMoneda === 'MXN' ? '1' : (prev.tipo_cambio && prev.tipo_cambio !== '1' ? prev.tipo_cambio : '')
      }))
      return
    }

    if (name === 'metodo_pago') {
      const formaDefault = formaPagoDefaultForMetodoPago(value)
      setFormData(prev => ({
        ...prev,
        metodo_pago: value,
        forma_pago: formaDefault,
      }))
      return
    }

    if (name === 'tipo_cambio') {
      if ((formData.moneda || 'MXN') === 'MXN') {
        setFormData(prev => ({ ...prev, tipo_cambio: '1' }))
        return
      }
      setFormData(prev => ({ ...prev, tipo_cambio: value }))
      return
    }

    // Si cambia el cliente EMISOR, resetear configuración y aplicar defaults de cliente
    if (name === 'client') {
      const clientData = clients?.find(c => c.id === parseInt(value))
      const emisorNombre = clientData ? (clientData.razon_social || clientData.business_name || `${clientData.nombre || ''} ${clientData.apellido_paterno || ''}`.trim()) : ''
      // API devuelve regimen_fiscal como ID (número) o como objeto; el form necesita el ID
      const r = clientData?.regimen_fiscal
      const emisorRegimenId = (r != null && typeof r === 'object' && r.id != null)
        ? r.id
        : (r != null && (typeof r === 'number' || (typeof r === 'string' && String(r).trim())))
          ? r
          : ''
      const emisorUso = getUsoCfdiEmisor(clients, value)
      setFormData(prev => ({
        ...prev,
        [name]: value,
        configuracion: '',
        uso_cfdi: resolveUsoCfdiPorTipoFactura(prev.tipo_factura, emisorUso) || prev.uso_cfdi,
        titulo_pdf: clientData?.nombre_comercial_pdf || emisorNombre || prev.titulo_pdf,
        emisor_regimen_fiscal: emisorRegimenId || prev.emisor_regimen_fiscal
      }))
      setSerieSeleccionada('')
      if (formData.tipo_factura === 'complemento') {
        setComplementoPaymentId('')
        setComplementoAplicacionIds([])
        setComplementoParcialidadById({})
      }
    } else if (name === 'receptor_cliente') {
      setTipoReceptor('seleccionado')
      // Si cambia el cliente RECEPTOR (de la lista)
      const clientData = clients?.find(c => c.id === parseInt(value))
      if (clientData && value) {
        // Régimen fiscal: la API devuelve regimen_fiscal como ID; usar regimen_fiscal_display
        // o buscar por regimen_fiscal_codigo en el catálogo
        let regimenStr = ''
        if (clientData.regimen_fiscal_display && String(clientData.regimen_fiscal_display).trim()) {
          regimenStr = String(clientData.regimen_fiscal_display).trim()
        } else if (clientData.regimen_fiscal && typeof clientData.regimen_fiscal === 'object' && (clientData.regimen_fiscal.codigo || clientData.regimen_fiscal.descripcion)) {
          regimenStr = `${clientData.regimen_fiscal.codigo || ''} - ${clientData.regimen_fiscal.descripcion || clientData.regimen_fiscal.nombre || ''}`.trim()
        } else if (clientData.regimen_fiscal_codigo && regimenesFiscales?.length) {
          const r = regimenesFiscales.find(x => (x.codigo || '').toString() === String(clientData.regimen_fiscal_codigo))
          regimenStr = r ? `${r.codigo} - ${r.descripcion}` : ''
        }
        // Autocompletar datos del receptor desde el cliente seleccionado
        setReceptorClienteId(value)
        setReceptorEsExterno(false)
        setReceptorData({
          rfc: clientData.rfc || '',
          razon_social: clientData.razon_social || clientData.business_name || 
                       `${clientData.nombre || ''} ${clientData.apellido_paterno || ''} ${clientData.apellido_materno || ''}`.trim() || '',
          direccion: [
            clientData.calle,
            clientData.numero_exterior ? `#${clientData.numero_exterior}` : '',
            clientData.numero_interior ? `Int. ${clientData.numero_interior}` : '',
            clientData.colonia,
            clientData.municipio,
            clientData.estado_fiscal,
            clientData.codigo_postal ? `C.P. ${clientData.codigo_postal}` : '',
            clientData.pais
          ].filter(Boolean).join(', ') || '',
          codigo_postal: clientData.codigo_postal || '',
          regimen_fiscal: regimenStr,
          email: clientData.email || '',
          residencia_fiscal: '',
          num_reg_id_trib: ''
        })
      } else {
        // Si se deselecciona el cliente receptor, marcar como externo
        setReceptorClienteId('')
        setReceptorEsExterno(true)
        setReceptorData({
          rfc: '',
          razon_social: '',
          direccion: '',
          codigo_postal: '',
          regimen_fiscal: '',
          email: '',
          residencia_fiscal: '',
          num_reg_id_trib: ''
        })
      }
    } else if (name.startsWith('receptor_') && name !== 'receptor_cliente') {
      // No cambiar tipoReceptor si está en Público Nacional/Extranjero (permite editar régimen fiscal)
      if (tipoReceptor !== 'publico_nacional' && tipoReceptor !== 'publico_extranjero') {
        setTipoReceptor('seleccionado')
      }
      // Manejar cambios en los campos manuales del receptor
      const receptorField = name.replace('receptor_', '')
      setReceptorData(prev => ({
        ...prev,
        [receptorField]: value
      }))
      // Si se modifica manualmente, marcar como externo si no había cliente seleccionado
      if (!receptorClienteId) {
        setReceptorEsExterno(true)
      }
    } else if (name === 'tipo_comprobante_pdf') {
      setFormData(prev => ({
        ...prev,
        tipo_comprobante_pdf: value,
      }))
      if (value !== 'recibo_arrendamiento') {
        setConceptos((prev) => prev.map((c) => ({ ...c, cuentas_predial: [''] })))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }
  
  const recalculateConceptTaxDetails = (concepto, decimals, satV2 = usaCalculoSatV2) => {
    const bruto = parseFloat(concepto.importe || 0)
    const desc = parseFloat(concepto.descuento || 0) || 0
    const base = satV2 ? Math.max(0, bruto - desc) : bruto
    const trasladados = (concepto.impuestos_trasladados || []).map((item) => {
      const tipoFactor = item.tipo_factor || 'Tasa'
      if (tipoFactor === 'Exento') {
        return { ...item, tipo_factor: 'Exento', tasa: 0, importe: 0 }
      }
      const tasaPct = parseFloat(item.tasa || 0)
      const importe = roundMoneyN(base * (tasaPct / 100), decimals)
      return { ...item, tipo_factor: tipoFactor, tasa: tasaPct, importe }
    })
    const retenidos = (concepto.retenciones_detalle || []).map((item) => {
      const tipoFactor = item.tipo_factor || 'Tasa'
      const tasaPct = parseFloat(item.tasa || 0)
      // Un importe fijado a mano NO se recalcula: este recálculo corre después de
      // guardar el modal, y sobrescribirlo devolvía el centavo al cálculo automático.
      if (item.importe_ajustado) {
        return { ...item, tipo_factor: tipoFactor, tasa: tasaPct, importe: parseFloat(item.importe) || 0 }
      }
      const importe = roundMoneyN(base * (tasaPct / 100), decimals)
      return { ...item, tipo_factor: tipoFactor, tasa: tasaPct, importe }
    })
    return { ...concepto, impuestos_trasladados: trasladados, retenciones_detalle: retenidos }
  }

  // El ajuste manual lo habilita la bandera de CUALQUIERA de los dos lados.
  // En la práctica se activa en el EMISOR --la contribuyente que factura a
  // dependencias--, porque los receptores de gobierno se capturan como RFC libre y
  // no existen como Client: no habría dónde ponerla.
  const receptorPermiteAjusteRetencion = useMemo(() => {
    const lista = clients || []
    const emisor = lista.find((c) => c.id === parseInt(formData.client, 10))
    if (emisor?.permite_ajuste_manual_retenciones) return true
    if (tipoReceptor !== 'seleccionado' || !receptorClienteId) return false
    const receptor = lista.find((c) => c.id === parseInt(receptorClienteId, 10))
    return Boolean(receptor?.permite_ajuste_manual_retenciones)
  }, [clients, formData.client, tipoReceptor, receptorClienteId])

  const permiteRetenciones = useMemo(() => {
    const lista = clients || []
    const emisor = lista.find((c) => c.id === parseInt(formData.client, 10))
    const emisorTipo = emisor?.type_tax_regime || tipoPersonaDesdeRfc(emisor?.rfc)
    const emisorRfc = emisor?.rfc
    let receptorTipo = null
    let receptorRfc = ''
    if (tipoReceptor === 'seleccionado' && receptorClienteId) {
      const receptor = lista.find((c) => c.id === parseInt(receptorClienteId, 10))
      receptorTipo = receptor?.type_tax_regime || tipoPersonaDesdeRfc(receptor?.rfc)
      receptorRfc = receptor?.rfc || ''
    } else {
      receptorRfc = receptorData?.rfc || ''
      receptorTipo = tipoPersonaDesdeRfc(receptorRfc)
    }
    return retencionesPermitidasPfAPm({
      emisorTipo,
      emisorRfc,
      receptorTipo,
      receptorRfc,
    })
  }, [clients, formData.client, tipoReceptor, receptorClienteId, receptorData?.rfc])

  const getTaxSummary = (rows) => {
    const totalsByCode = {}
    rows.forEach((r) => {
      const code = (r.impuesto || '').trim()
      const amount = parseFloat(r.importe || 0) || 0
      if (!code || amount <= 0) return
      totalsByCode[code] = (totalsByCode[code] || 0) + amount
    })
    const labels = Object.keys(totalsByCode).map((c) => IMPUESTO_LABEL_BY_CODE[c] || c)
    const total = Object.values(totalsByCode).reduce((s, v) => s + v, 0)
    return { labels, total: roundMoneyN(total, decimalesInput) }
  }

  const openTaxModal = (index, type) => {
    if (type === 'retencion' && !permiteRetenciones) {
      toast.error(
        'Las retenciones solo aplican cuando el emisor es persona física nacional y el receptor es persona moral nacional.'
      )
      return
    }
    const concepto = conceptos[index]
    if (type === 'traslado' && OBJETO_IMP_SIN_DESGLOSE.has(concepto.objeto_imp || OBJETO_IMP_DEFAULT)) {
      toast.error(
        `Con ObjetoImp ${concepto.objeto_imp} no se captura desglose de impuestos. Cambie el objeto de impuesto si requiere traslados.`
      )
      return
    }
    const rows = type === 'traslado'
      ? (concepto.impuestos_trasladados || [])
      : (concepto.retenciones_detalle || [])
    setTaxConceptIndex(index)
    setTaxModalType(type)
    setTaxDraftRows(rows.map((r) => ({
      ...r,
      tipo_factor: r.tipo_factor || 'Tasa',
    })))
    setShowTaxModal(true)
  }

  const handleConceptoChange = (index, field, value) => {
    const newConceptos = [...conceptos]
    newConceptos[index] = {
      ...newConceptos[index],
      [field]: value
    }

    if (field === 'objeto_imp') {
      if (OBJETO_IMP_SIN_DESGLOSE.has(value)) {
        newConceptos[index].impuestos_trasladados = []
        newConceptos[index].retenciones_detalle = []
        newConceptos[index].iva_trasladado = 0
        newConceptos[index].iva_retenido = 0
        newConceptos[index].retencion_impuesto = ''
      }
      setConceptos(newConceptos)
      calculateTotals(newConceptos)
      return
    }

    // Si cambia la clave SAT, validarla y buscar descripción
    if (field === 'clave_producto_servicio' && value) {
      validarClaveSAT(index, value.trim())
      // Auto-asignar cuenta solo si no tiene ya una seleccionada
      const tieneCuenta = cuentasAsignadas[index] || cuentasAsignadas[String(index)]
      if (!tieneCuenta && formData.client) {
        obtenerCuentaAsignada(index)
      }
    }
    
    // Auto-asignación por mapeo solo para Clave SAT (no por descripción)
    
    // Calcular importe (v2_sat: bruto = cantidad × precio; descuento aparte)
    if (field === 'cantidad' || field === 'precio_unitario' || field === 'descuento') {
      const cantidad = parseFloat(newConceptos[index].cantidad || 0)
      const precio = parseFloat(newConceptos[index].precio_unitario || 0)
      const descuento = parseFloat(newConceptos[index].descuento || 0)
      const bruto = cantidad * precio
      const importe = usaCalculoSatV2 ? bruto : (bruto - descuento)
      
      newConceptos[index].importe = roundMoneyN(importe, decimalesInput)
      newConceptos[index] = recalculateConceptTaxDetails(newConceptos[index], decimalesInput)
      newConceptos[index].iva_trasladado = roundMoneyN(
        (newConceptos[index].impuestos_trasladados || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
        decimalesInput
      )
      newConceptos[index].iva_retenido = roundMoneyN(
        (newConceptos[index].retenciones_detalle || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
        decimalesInput
      )
      if ((newConceptos[index].retenciones_detalle || []).length > 0) {
        newConceptos[index].retencion_impuesto = (newConceptos[index].retenciones_detalle[0]?.impuesto || '').trim()
      } else {
        newConceptos[index].retencion_impuesto = ''
      }

      // Validar que el importe no sea negativo
      if (newConceptos[index].importe < 0) {
        newConceptos[index].importe = 0
      }
    }
    
    setConceptos(newConceptos)
    calculateTotals(newConceptos)
  }

  const onClaveUnidadFocus = (index, e) => {
    claveUnidadSnapshotRef.current[index] = e.target.value
  }

  const onClaveUnidadBlur = async (index, e) => {
    const raw = e.target.value
    const snap = claveUnidadSnapshotRef.current[index]
    if (raw === snap) {
      const t = raw.trim()
      if (raw !== t) {
        handleConceptoChange(index, 'clave_unidad', t)
      }
      return
    }
    const trimmed = raw.trim()
    if (!trimmed) {
      setConceptos((prev) => {
        const c = [...prev]
        if (c[index]) {
          c[index].clave_unidad = ''
          c[index].unidad_medida = ''
        }
        calculateTotals(c)
        return c
      })
      claveUnidadSnapshotRef.current[index] = ''
      return
    }
    try {
      const params = new URLSearchParams({ codigo: trimmed })
      const res = await api.get(`/financial/invoices/catalogo-clave-unidad/?${params.toString()}`)
      const nombre = res.data?.nombre ?? ''
      setConceptos((prev) => {
        const c = [...prev]
        if (c[index]) {
          c[index].clave_unidad = trimmed
          c[index].unidad_medida = nombre
        }
        calculateTotals(c)
        return c
      })
      claveUnidadSnapshotRef.current[index] = trimmed
    } catch (err) {
      if (err.response?.status === 404) {
        setConceptos((prev) => {
          const c = [...prev]
          if (c[index]) {
            c[index].clave_unidad = ''
            c[index].unidad_medida = ''
          }
          calculateTotals(c)
          return c
        })
        claveUnidadSnapshotRef.current[index] = ''
      } else {
        toast.error('No se pudo validar la clave de unidad')
      }
    }
  }

  // Validar clave SAT
  const validarClaveSAT = async (index, clave, skipDescriptionUpdate = false) => {
    if (!clave || clave.length < 3) {
      setValidacionClave(prev => ({ ...prev, [index]: null }))
      return
    }
    
    try {
      const params = new URLSearchParams({
        clave: clave
      })
      if (formData.client) {
        params.append('cliente_id', formData.client)
      }
      
      const response = await api.get(`/financial/invoices/validar_clave_sat/?${params.toString()}`)
      
      if (response.data.valida) {
        setValidacionClave(prev => ({ ...prev, [index]: { valida: true, info: response.data.info || `Clave SAT válida: ${clave}` } }))
        
        // Auto-completar descripción solo si no se omite y está vacía
        if (!skipDescriptionUpdate) {
          setConceptos(prev => {
            const newConceptos = [...prev]
            if (newConceptos[index] && (!newConceptos[index].descripcion || newConceptos[index].descripcion.trim() === '')) {
              newConceptos[index].descripcion = response.data.descripcion || ''
              calculateTotals(newConceptos)
            }
            return newConceptos
          })
        }
      } else {
        setValidacionClave(prev => ({ ...prev, [index]: { valida: false, error: response.data.error || 'Clave SAT no válida' } }))
      }
    } catch (error) {
      console.error('Error validando clave SAT:', error)
      const errorMessage = error.response?.data?.error || error.message || 'Error al validar clave SAT'
      setValidacionClave(prev => ({ ...prev, [index]: { valida: false, error: errorMessage } }))
    }
  }
  
  // Abrir modal de búsqueda de claves SAT
  const abrirModalClaveSAT = (index) => {
    setConceptoIndexModal(index)
    setBusquedaModalClave(conceptos[index]?.clave_producto_servicio || '')
    setMostrarModalClaveSAT(true)
    // Si hay una clave, buscar automáticamente
    if (conceptos[index]?.clave_producto_servicio) {
      buscarClavesModal(conceptos[index].clave_producto_servicio)
    }
  }

  // Cerrar modal de búsqueda
  const cerrarModalClaveSAT = () => {
    setMostrarModalClaveSAT(false)
    setConceptoIndexModal(null)
    setBusquedaModalClave('')
    setResultadosModalClave([])
  }

  // Buscar claves SAT en el modal
  const buscarClavesModal = async (query) => {
    if (!query || query.trim().length < 2) {
      setResultadosModalClave([])
      return
    }

    // Limpiar timeout anterior
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Debounce de 300ms
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        setBuscandoModalClave(true)
        const params = new URLSearchParams({
          q: query.trim(),
          limit: '50' // Mostrar más resultados en el modal
        })
        if (formData.client) {
          params.append('cliente_id', formData.client)
        }

        const response = await api.get(`/financial/invoices/buscar_claves_sat/?${params.toString()}`)
        setResultadosModalClave(response.data.results || [])
      } catch (error) {
        console.error('Error buscando claves SAT:', error)
        setResultadosModalClave([])
        toast.error('Error al buscar claves SAT')
      } finally {
        setBuscandoModalClave(false)
      }
    }, 300)
  }

  // Seleccionar clave SAT del modal
  const seleccionarClaveSAT = async (sugerencia) => {
    if (conceptoIndexModal === null) return

    const index = conceptoIndexModal
    
    // Cerrar modal primero para evitar conflictos de estado
    cerrarModalClaveSAT()
    
    // Actualizar el concepto con la clave seleccionada
    setConceptos(prev => {
      const newConceptos = [...prev]
      if (newConceptos[index]) {
        newConceptos[index].clave_producto_servicio = sugerencia.clave
        if (!newConceptos[index].descripcion || newConceptos[index].descripcion.trim() === '') {
          newConceptos[index].descripcion = sugerencia.descripcion || ''
        }
        calculateTotals(newConceptos)
      }
      return newConceptos
    })
    
    // Última clave_unidad usada con esta clave SAT (global); validar contra catálogo c_ClaveUnidad
    try {
      const params = new URLSearchParams({ clave_sat: sugerencia.clave })
      const res = await api.get(`/financial/invoices/obtener_clave_unidad_por_clave_sat/?${params.toString()}`)
      const ku = (res.data?.clave_unidad || '').trim()
      if (ku) {
        try {
          const r2 = await api.get(
            `/financial/invoices/catalogo-clave-unidad/?${new URLSearchParams({ codigo: ku }).toString()}`
          )
          const nombre = r2.data?.nombre ?? ''
          setConceptos((prev) => {
            const newConceptos = [...prev]
            if (newConceptos[index]) {
              newConceptos[index].clave_unidad = ku
              newConceptos[index].unidad_medida = nombre
            }
            calculateTotals(newConceptos)
            return newConceptos
          })
          claveUnidadSnapshotRef.current[index] = ku
        } catch (e2) {
          if (e2.response?.status === 404) {
            setConceptos((prev) => {
              const newConceptos = [...prev]
              if (newConceptos[index]) {
                newConceptos[index].clave_unidad = ''
                newConceptos[index].unidad_medida = ''
              }
              calculateTotals(newConceptos)
              return newConceptos
            })
            claveUnidadSnapshotRef.current[index] = ''
          } else {
            console.error(e2)
            toast.error('No se pudo validar la clave de unidad sugerida')
          }
        }
      }
    } catch (err) {
      // Sin sugerencia histórica o error del endpoint
    }
    
    // Validar la clave (skipDescriptionUpdate = true porque ya la actualizamos arriba)
    try {
      await validarClaveSAT(index, sugerencia.clave, true)
    } catch (error) {
      console.error('Error validando clave SAT:', error)
      // Si falla la validación, marcamos como válida de todas formas (viene del catálogo)
      setValidacionClave(prev => ({ 
        ...prev, 
        [index]: { 
          valida: true, 
          info: `Clave SAT: ${sugerencia.clave}` 
        } 
      }))
    }
    
    // Obtener cuenta asignada después de un pequeño delay para asegurar que el estado se actualizó
    setTimeout(() => {
      obtenerCuentaAsignada(index)
    }, 100)
  }
  
  // Obtener cuenta contable asignada para un concepto
  const obtenerCuentaAsignada = async (index) => {
    const concepto = conceptos[index]
    if (!formData.client || (!concepto.clave_producto_servicio && !concepto.descripcion)) {
      setCuentasAsignadas(prev => ({ ...prev, [index]: null }))
      return
    }
    
    try {
      const params = new URLSearchParams({
        cliente_id: formData.client
      })
      if (concepto.clave_producto_servicio) {
        params.append('clave_sat', concepto.clave_producto_servicio)
      }
      if (concepto.descripcion) {
        params.append('descripcion', concepto.descripcion)
      }
      
      const response = await api.get(`/financial/invoices/obtener_cuenta_mapeada/?${params.toString()}`)
      
      if (response.data.cuenta_asignada) {
        setCuentasAsignadas(prev => ({ ...prev, [index]: response.data.cuenta_asignada }))
      } else {
        setCuentasAsignadas(prev => ({ ...prev, [index]: null }))
      }
    } catch (error) {
      // No mostrar error, simplemente no hay cuenta asignada
      setCuentasAsignadas(prev => ({ ...prev, [index]: null }))
    }
  }
  
  // Abrir modal de búsqueda de cuentas contables
  const abrirModalCuenta = (index) => {
    setConceptoIndexModalCuenta(index)
    // Si ya hay una cuenta asignada, mostrar su código en la búsqueda
    const cuentaActual = cuentasAsignadas[index] || cuentasAsignadas[String(index)]
    setBusquedaModalCuenta(cuentaActual?.code || '')
    setMostrarModalCuenta(true)
    
    // Si hay una cuenta asignada, buscar automáticamente
    if (cuentaActual?.code) {
      buscarCuentasModal(cuentaActual.code)
    }
  }

  // Cerrar modal de búsqueda de cuentas
  const cerrarModalCuenta = () => {
    setMostrarModalCuenta(false)
    setConceptoIndexModalCuenta(null)
    setBusquedaModalCuenta('')
    setResultadosModalCuenta([])
  }

  // Buscar cuentas contables en el modal
  const buscarCuentasModal = async (query) => {
    if (!query || query.trim().length < 2) {
      setResultadosModalCuenta([])
      return
    }

    // Limpiar timeout anterior
    if (debounceTimeoutCuentaRef.current) {
      clearTimeout(debounceTimeoutCuentaRef.current)
    }

    // Debounce de 300ms
    debounceTimeoutCuentaRef.current = setTimeout(async () => {
      try {
        setBuscandoModalCuenta(true)
        const params = new URLSearchParams({
          search: query.trim(),
          is_active: 'true'
        })
        if (formData.client) {
          params.append('cliente_id', formData.client)
        }

        const response = await api.get(`/accounting/accounts/?${params.toString()}`)
        setResultadosModalCuenta(response.data.results || response.data || [])
      } catch (error) {
        console.error('Error buscando cuentas contables:', error)
        setResultadosModalCuenta([])
        toast.error('Error al buscar cuentas contables')
      } finally {
        setBuscandoModalCuenta(false)
      }
    }, 300)
  }

  // Seleccionar cuenta contable del modal
  const seleccionarCuenta = (cuenta) => {
    if (conceptoIndexModalCuenta === null || !cuenta || !cuenta.id) {
      toast.error('Error al seleccionar cuenta')
      return
    }

    const index = conceptoIndexModalCuenta
    // Para cuentas personalizadas (pers_X), usar cuenta_base_id para persistir en BD
    const realId = (cuenta.cuenta_base_id != null && cuenta.cuenta_base_id !== '')
      ? cuenta.cuenta_base_id
      : (typeof cuenta.id === 'number' ? cuenta.id : parseInt(cuenta.id, 10))

    const cuentaAsignada = {
      id: realId,
      code: (cuenta.code || cuenta.codigo || String(cuenta.id)).trim(),
      name: (cuenta.name || cuenta.nombre || 'Sin nombre').trim()
    }
    
    console.log('Seleccionando cuenta:', cuentaAsignada, 'para índice:', index, 'tipo:', typeof index)
    console.log('Estado actual de cuentasAsignadas antes:', cuentasAsignadas)
    
    // Actualizar la cuenta asignada usando función de actualización (crear nuevo objeto)
    // Guardar tanto con índice numérico como string para compatibilidad
    setCuentasAsignadas(prev => {
      const nuevo = {
        ...prev,
        [index]: { ...cuentaAsignada },
        [String(index)]: { ...cuentaAsignada }
      }
      console.log('Nuevo estado de cuentasAsignadas:', nuevo)
      return nuevo
    })
    
    // Cerrar modal primero
    cerrarModalCuenta()
    
    // Mostrar mensaje después de un pequeño delay
    setTimeout(() => {
      toast.success(`Cuenta ${cuentaAsignada.code} asignada al concepto`)
    }, 150)
  }

  const addTaxDraftRow = () => {
    const allowed = IMPUESTO_CATALOGO.filter((i) => (taxModalType === 'traslado' ? i.traslado : i.retencion))
    setTaxDraftRows((prev) => [...prev, {
      impuesto: allowed[0]?.codigo || '',
      descripcion: allowed[0]?.etiqueta || '',
      tipo_factor: 'Tasa',
      tasa: taxModalType === 'traslado' ? 16 : 0,
      importe: 0,
    }])
  }

  const updateTaxDraftRow = (idx, field, value) => {
    setTaxDraftRows((prev) => prev.map((r, i) => {
      if (i !== idx) return r
      const row = { ...r, [field]: value }
      if (field === 'impuesto') {
        row.descripcion = IMPUESTO_LABEL_BY_CODE[value] || value
      }
      if (field === 'tipo_factor' && value === 'Exento') {
        row.tasa = 0
        row.importe = 0
        row.impuesto = '002'
        row.descripcion = IMPUESTO_LABEL_BY_CODE['002'] || 'IVA'
      }
      return row
    }))
  }

  const removeTaxDraftRow = (idx) => {
    setTaxDraftRows((prev) => prev.filter((_, i) => i !== idx))
  }

  const saveTaxDraftRows = () => {
    if (taxConceptIndex === null) return
    if (taxModalType === 'retencion' && !permiteRetenciones) {
      toast.error(
        'Las retenciones solo aplican cuando el emisor es persona física nacional y el receptor es persona moral nacional.'
      )
      return
    }
    const allowedCodes = IMPUESTO_CATALOGO
      .filter((i) => (taxModalType === 'traslado' ? i.traslado : i.retencion))
      .map((i) => i.codigo)
    const seen = new Set()
    const validRows = []
    for (const row of taxDraftRows) {
      const code = (row.impuesto || '').trim()
      if (!allowedCodes.includes(code)) {
        toast.error(`Impuesto SAT no permitido para ${taxModalType === 'traslado' ? 'traslado' : 'retención'}: ${code || 'vacío'}`)
        return
      }
      if (seen.has(code)) {
        toast.error(`No se permite repetir el impuesto ${IMPUESTO_LABEL_BY_CODE[code] || code} en el mismo concepto`)
        return
      }
      seen.add(code)
      let tipoFactor = row.tipo_factor || 'Tasa'
      if (!['Tasa', 'Cuota', 'Exento'].includes(tipoFactor)) {
        toast.error('TipoFactor no válido. Use Tasa, Cuota o Exento.')
        return
      }
      let impCode = code
      if (tipoFactor === 'Exento') {
        if (taxModalType !== 'traslado') {
          toast.error('TipoFactor Exento solo aplica a traslados de IVA.')
          return
        }
        impCode = '002'
      }
      const tasa = tipoFactor === 'Exento' ? 0 : (parseFloat(row.tasa || 0) || 0)
      if (tasa < 0) {
        toast.error('La tasa no puede ser negativa')
        return
      }
      const fila = {
        impuesto: impCode,
        descripcion: IMPUESTO_LABEL_BY_CODE[impCode] || impCode,
        tipo_factor: tipoFactor,
        tasa,
        importe: 0,
      }
      if (taxModalType === 'retencion' && row.importe_ajustado) {
        const baseConcepto = parseFloat(conceptos[taxConceptIndex]?.importe || 0) || 0
        if (importeFueraDeRango(baseConcepto, tasa, row.importe)) {
          const { inferior, superior } = rangoImporteRetencion(baseConcepto, tasa)
          toast.error(
            `El importe ${row.importe} de ${IMPUESTO_LABEL_BY_CODE[impCode] || impCode} queda fuera de lo que el SAT admite. ` +
            `Para esta base y tasa solo acepta entre ${inferior.toFixed(2)} y ${superior.toFixed(2)}.`
          )
          return
        }
        fila.importe = parseFloat(row.importe) || 0
        fila.importe_ajustado = true
      }
      validRows.push(fila)
    }
    const newConceptos = [...conceptos]
    const current = { ...newConceptos[taxConceptIndex] }
    if (taxModalType === 'traslado') {
      current.impuestos_trasladados = validRows
      // IVA (0/8/16/Exento), IEPS o cualquier traslado → ObjetoImp 02
      if (validRows.length > 0) {
        current.objeto_imp = '02'
      } else if (!current.objeto_imp) {
        current.objeto_imp = OBJETO_IMP_DEFAULT
      }
    } else {
      current.retenciones_detalle = validRows
      if (validRows.length > 0) {
        current.objeto_imp = '02'
      }
    }
    const recalculated = recalculateConceptTaxDetails(current, decimalesInput)
    recalculated.iva_trasladado = roundMoneyN(
      (recalculated.impuestos_trasladados || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
      decimalesInput
    )
    recalculated.iva_retenido = roundMoneyN(
      (recalculated.retenciones_detalle || []).reduce((s, t) => s + (parseFloat(t.importe || 0) || 0), 0),
      decimalesInput
    )
    recalculated.retencion_impuesto = (recalculated.retenciones_detalle?.[0]?.impuesto || '').trim()
    newConceptos[taxConceptIndex] = recalculated
    setConceptos(newConceptos)
    calculateTotals(newConceptos)
    setShowTaxModal(false)
  }

  const addConcepto = () => {
    setConceptos([...conceptos, {
      cantidad: 1,
      no_identificacion: '',
      clave_unidad: '',
      unidad_medida: '',
      clave_producto_servicio: '',
      objeto_imp: OBJETO_IMP_DEFAULT,
      descripcion: '',
      precio_unitario: 0,
      importe: 0,
      descuento: 0,
      iva_trasladado: 0,
      iva_retenido: 0,
      retencion_impuesto: '',
      impuestos_trasladados: [],
      retenciones_detalle: [],
      cuentas_predial: [''],
      orden: conceptos.length + 1
    }])
  }

  const updateCuentaPredial = (conceptoIndex, predialIndex, value) => {
    const cleaned = String(value || '').replace(/[^0-9a-zA-Z]/g, '').slice(0, 150)
    const newConceptos = [...conceptos]
    const list = Array.isArray(newConceptos[conceptoIndex].cuentas_predial)
      ? [...newConceptos[conceptoIndex].cuentas_predial]
      : ['']
    list[predialIndex] = cleaned
    newConceptos[conceptoIndex] = { ...newConceptos[conceptoIndex], cuentas_predial: list }
    setConceptos(newConceptos)
  }

  const addCuentaPredial = (conceptoIndex) => {
    const newConceptos = [...conceptos]
    const list = Array.isArray(newConceptos[conceptoIndex].cuentas_predial)
      ? [...newConceptos[conceptoIndex].cuentas_predial]
      : []
    list.push('')
    newConceptos[conceptoIndex] = { ...newConceptos[conceptoIndex], cuentas_predial: list }
    setConceptos(newConceptos)
  }

  const removeCuentaPredial = (conceptoIndex, predialIndex) => {
    const newConceptos = [...conceptos]
    let list = Array.isArray(newConceptos[conceptoIndex].cuentas_predial)
      ? [...newConceptos[conceptoIndex].cuentas_predial]
      : []
    list.splice(predialIndex, 1)
    if (list.length === 0) list = ['']
    newConceptos[conceptoIndex] = { ...newConceptos[conceptoIndex], cuentas_predial: list }
    setConceptos(newConceptos)
  }

  const removeConcepto = (index) => {
    const newConceptos = conceptos.filter((_, i) => i !== index)
    setConceptos(newConceptos)
    calculateTotals(newConceptos)
    
    // Limpiar cuenta asignada del concepto eliminado
    setCuentasAsignadas(prev => {
      const nuevo = { ...prev }
      delete nuevo[index]
      // Reindexar las cuentas asignadas para los conceptos restantes
      const reindexado = {}
      Object.keys(nuevo).forEach(key => {
        const oldIndex = parseInt(key)
        if (oldIndex > index) {
          reindexado[oldIndex - 1] = nuevo[oldIndex]
        } else if (oldIndex < index) {
          reindexado[oldIndex] = nuevo[oldIndex]
        }
      })
      return reindexado
    })
  }

  const calculateTotals = (conceptosList) => {
    const subtotalRaw = conceptosList.reduce((sum, c) => sum + parseFloat(c.importe || 0), 0)
    const descuentoRaw = conceptosList.reduce((sum, c) => sum + parseFloat(c.descuento || 0), 0)
    const taxRaw = conceptosList.reduce((sum, c) => sum + parseFloat(c.iva_trasladado || 0), 0)
    // El total resta el importe REALMENTE emitido, incluido el ajustado a mano. El
    // PAC valida Total = SubTotal − Desc + Traslados − Retenidos **exactamente**
    // (CFDI40119), así que cerrar con la retención teórica produciría un rechazo.
    const retRaw = conceptosList.reduce((sum, c) => sum + parseFloat(c.iva_retenido || 0), 0)
    // Total desde montos de línea (6 dec) + un solo redondeo global a moneda.
    // No sumar componentes ya redondeados a 2 (evita 14299.99 vs 14300.00).
    const totalRaw = usaCalculoSatV2
      ? (subtotalRaw - descuentoRaw + taxRaw - retRaw)
      : (subtotalRaw + taxRaw - retRaw)
    const subtotal = roundMoneyN(subtotalRaw, decimalesDisplay)
    const descuento_total = usaCalculoSatV2 ? roundMoneyN(descuentoRaw, decimalesDisplay) : 0
    const tax_amount = roundMoneyN(taxRaw, decimalesDisplay)
    // Si el total recalculado solo difiere del que ya tiene la factura por un centavo
    // de redondeo, se conserva el persistido. El backend ya hace esto al timbrar
    // (`recalcular_totales_cfdi_desde_conceptos`: «preferir el total ya persistido»);
    // sin esta mitad, abrir y guardar una factura existente le movía el total un
    // centavo, porque aquí se suman líneas de 6 decimales con un solo redondeo final
    // y el comprobante original pudo cerrarse sumando componentes ya redondeados.
    const total = roundMoneyN(totalRaw, decimalesDisplay)

    setFormData(prev => ({
      ...prev,
      subtotal,
      descuento_total,
      tax_amount,
      total
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const newErrors = {}

    // Validar cliente (emisor)
    if (!formData.client) {
      newErrors.client = 'Cliente (Emisor) es requerido'
    }
    
    // Validar configuración de facturación
    if (!formData.configuracion && configuraciones && configuraciones.length > 0) {
      // Si hay configuraciones disponibles, debe seleccionarse una
      if (!serieSeleccionada || serieSeleccionada === '') {
        // Si no hay serie seleccionada, buscar configuraciones sin serie
        const configSinSerie = configuraciones.find(c => !c.serie || c.serie === '')
        if (!configSinSerie) {
          newErrors.configuracion = 'Debe seleccionar una serie o configuración de facturación'
          newErrors.serie = 'Debe seleccionar una serie o configuración de facturación'
        } else {
          // Auto-seleccionar la configuración sin serie si existe solo una
          const configsSinSerie = configuraciones.filter(c => !c.serie || c.serie === '')
          if (configsSinSerie.length === 1) {
            setFormData(prev => ({
              ...prev,
              configuracion: configsSinSerie[0].id
            }))
            setSerieSeleccionada('')
          } else {
            newErrors.configuracion = 'Hay múltiples configuraciones sin serie, seleccione una'
          }
        }
      } else {
        newErrors.configuracion = 'Debe seleccionar una configuración de facturación'
      }
    }
    
    // Validar datos del receptor
    if (!receptorData.rfc || receptorData.rfc.trim().length < 10) {
      newErrors.receptor_rfc = 'RFC del receptor es requerido (mínimo 10 caracteres)'
    }
    if (!receptorData.razon_social || receptorData.razon_social.trim() === '') {
      newErrors.receptor_razon_social = 'Razón social del receptor es requerida'
    }
    // Público extranjero: el CFDI solo exige CP (= emisor); dirección/email no se copian del emisor.
    if (tipoReceptor !== 'publico_extranjero' && (!receptorData.direccion || receptorData.direccion.trim() === '')) {
      newErrors.receptor_direccion = 'Dirección fiscal del receptor es requerida'
    }
    if (!receptorData.codigo_postal || receptorData.codigo_postal.trim().length < 5) {
      newErrors.receptor_codigo_postal = 'Código postal del receptor es requerido (5 dígitos)'
    }
    if (tipoReceptor === 'publico_extranjero') {
      const emisorVal = clients?.find((c) => c.id === parseInt(formData.client, 10))
      const ecp = (emisorVal?.codigo_postal || '').toString().trim().slice(0, 5)
      const rcp = (receptorData.codigo_postal || '').trim().slice(0, 5)
      if (ecp.length >= 5 && rcp !== ecp) {
        newErrors.receptor_codigo_postal = 'El código postal del receptor debe coincidir con el del emisor.'
      }
      if (!(receptorData.residencia_fiscal || '').trim()) {
        newErrors.receptor_residencia_fiscal = 'Seleccione residencia fiscal (c_Pais).'
      } else {
        const res = (receptorData.residencia_fiscal || '').trim().toUpperCase()
        if (res === 'MEX') {
          newErrors.receptor_residencia_fiscal = 'Para público extranjero no seleccione MEX.'
        } else if ((catalogoPaises || []).length > 0 && !(catalogoPaises || []).some((p) => p.codigo === res)) {
          newErrors.receptor_residencia_fiscal = 'La clave de país no está en el catálogo SAT.'
        }
      }
      if (!(receptorData.num_reg_id_trib || '').trim()) {
        newErrors.receptor_num_reg_id_trib = 'Indique el número de identificación fiscal en el país.'
      }
    }

    // Validar uso CFDI
    if (!formData.uso_cfdi || formData.uso_cfdi.trim() === '') {
      newErrors.uso_cfdi = 'Uso CFDI es requerido'
    }
    if (!formData.moneda || String(formData.moneda).trim() === '') {
      newErrors.moneda = 'Moneda es requerida'
    }
    if ((formData.moneda || 'MXN') !== 'MXN') {
      const tc = parseFloat(formData.tipo_cambio)
      if (!Number.isFinite(tc) || tc <= 0) {
        newErrors.tipo_cambio = 'Tipo de cambio debe ser mayor a 0 para monedas distintas a MXN'
      }
    }
    if (!esComplemento && (formData.metodo_pago || 'PUE') === 'PUE' && (formData.forma_pago || '') === '99') {
      newErrors.forma_pago = 'Con PUE la forma de pago no puede ser 99. Use PPD si el pago es diferido.'
    }
    
    // Validar fechas
    if (!formData.issue_date) {
      newErrors.issue_date = 'Fecha de emisión es requerida'
    } else if (!invoice?.cfdi_uuid && !isIssueDateWithinWindowMx(formData.issue_date)) {
      newErrors.issue_date = ISSUE_DATE_WINDOW_MESSAGE
    }
    if (!formData.due_date) {
      newErrors.due_date = 'Fecha de vencimiento es requerida'
    }
    const requiereInformacionGlobal = (
      !esComplemento &&
      (
        formData.tipo_factura === 'global' ||
        (
          tipoReceptor === 'publico_nacional' ||
          ((receptorData.rfc || '').toUpperCase().trim() === 'XAXX010101000')
        )
      )
    )
    if (requiereInformacionGlobal) {
      if (!(formData.global_periodicidad || '').toString().trim()) {
        newErrors.global_periodicidad = 'Seleccione la periodicidad (catálogo SAT) para factura global o Público en General'
      }
      if (!(formData.global_meses || '').toString().trim()) {
        newErrors.global_meses = 'Seleccione mes o periodo (catálogo SAT c_Meses 01-18) para factura global o Público en General'
      }
      if (!formData.global_anio || formData.global_anio < 2000 || formData.global_anio > 2100) {
        newErrors.global_anio = 'El año es obligatorio para factura global o Público en General (entre 2000 y 2100)'
      }
    }
    
    // Validar que fecha de vencimiento no sea anterior a fecha de emisión
    if (formData.issue_date && formData.due_date) {
      const issueDate = parseApiDateOnly(formData.issue_date)
      const dueDate = parseApiDateOnly(formData.due_date)
      if (issueDate && dueDate && dueDate < issueDate) {
        newErrors.due_date = 'La fecha de vencimiento no puede ser anterior a la fecha de emisión'
      }
    }

    if (invoice && formData.tipo_factura === 'complemento' && !invoiceToUse?.cfdi_uuid) {
      if (!complementoAplicacionIds || complementoAplicacionIds.length === 0) {
        newErrors.complemento_aplicaciones = 'Seleccione al menos un documento relacionado'
      }
      for (const aid of (complementoAplicacionIds || [])) {
        const p = complementoParcialidadById[aid]
        if (!Number.isFinite(Number(p)) || Number(p) < 1) {
          newErrors.complemento_parcialidad = 'Cada documento relacionado debe tener parcialidad ≥ 1'
          break
        }
      }
    }
    
    // Validar tipo de factura según configuración
    if (configuracionSeleccionada && configuracionSeleccionada.tipos_permitidos && configuracionSeleccionada.tipos_permitidos.length > 0) {
      if (!configuracionSeleccionada.tipos_permitidos.includes(formData.tipo_factura)) {
        newErrors.tipo_factura = `El tipo de factura '${formData.tipo_factura}' no está permitido en esta configuración`
      }
    }
    
    // Validar monto máximo si está configurado
    if (configuracionSeleccionada && configuracionSeleccionada.monto_maximo) {
      const sub = conceptos.reduce((sum, c) => sum + parseFloat(c.importe || 0), 0)
      const desc = conceptos.reduce((sum, c) => sum + parseFloat(c.descuento || 0), 0)
      const tax = conceptos.reduce((sum, c) => sum + parseFloat(c.iva_trasladado || 0), 0)
      const ret = conceptos.reduce((sum, c) => sum + parseFloat(c.iva_retenido || 0), 0)
      const totalCalculado = usaCalculoSatV2
        ? roundMoneyN(sub - desc + tax - ret, decimalesInput)
        : roundMoneyN(sub + tax - ret, decimalesInput)
      
      const montoMaximo = parseFloat(configuracionSeleccionada.monto_maximo)
      if (totalCalculado > montoMaximo) {
        newErrors.total = `El total ($${totalCalculado.toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}) excede el monto máximo permitido ($${montoMaximo.toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })})`
      }
    }
    
    if ((formData.tipo_comprobante || 'I') === 'E') {
      const relOk = cfdisRelacionados.some((r) => (r.uuid || '').trim() && (r.tipo_relacion || '').trim())
      if (!relOk) {
        newErrors.cfdis_relacionados = 'La nota de crédito (Tipo E) requiere al menos una factura relacionada (UUID).'
      }
    }

    // Validar conceptos (complemento de pago: concepto fijo con importe 0)
    if (formData.tipo_factura === 'complemento') {
      if (conceptos.length === 0) {
        newErrors.conceptos = 'El complemento debe incluir el concepto de pago'
      } else {
        conceptos.forEach((concepto, index) => {
          if (!(concepto.clave_unidad || '').trim()) {
            newErrors.conceptos = `ClaveUnidad es obligatorio en cada concepto. El concepto ${index + 1} no tiene ClaveUnidad.`
          }
          if (!concepto.descripcion || concepto.descripcion.trim() === '') {
            newErrors.conceptos = `La descripción del concepto ${index + 1} es requerida`
          }
        })
      }
    } else if (conceptos.length === 0) {
      newErrors.conceptos = 'Debe agregar al menos un concepto'
    } else {
      // Validar cada concepto
      conceptos.forEach((concepto, index) => {
        if (!(concepto.clave_unidad || '').trim()) {
          newErrors.conceptos = `ClaveUnidad es obligatorio en cada concepto. El concepto ${index + 1} no tiene ClaveUnidad.`
        }
        if (!concepto.descripcion || concepto.descripcion.trim() === '') {
          newErrors.conceptos = `La descripción del concepto ${index + 1} es requerida`
        }
        const trasRows = Array.isArray(concepto.impuestos_trasladados) ? concepto.impuestos_trasladados : []
        const retRows = Array.isArray(concepto.retenciones_detalle) ? concepto.retenciones_detalle : []
        const objetoImp = (concepto.objeto_imp || OBJETO_IMP_DEFAULT).trim()
        const hasDup = (rows) => {
          const seen = new Set()
          for (const r of rows) {
            const code = (r.impuesto || '').trim()
            if (seen.has(code)) return true
            seen.add(code)
          }
          return false
        }
        if (hasDup(trasRows) || hasDup(retRows)) {
          newErrors.conceptos = `No se permiten impuestos/retenciones duplicados en el concepto ${index + 1}`
        }
        if (!objetoImp) {
          newErrors.conceptos = `Concepto ${index + 1}: ObjetoImp es obligatorio.`
        }
        const tieneIvaIepsORet = retRows.length > 0 || trasRows.some((r) => {
          const imp = (r.impuesto || '').trim()
          const tf = r.tipo_factor || 'Tasa'
          return imp === '002' || imp === '003' || tf === 'Exento'
        })
        if (tieneIvaIepsORet && objetoImp !== '02') {
          newErrors.conceptos = `Concepto ${index + 1}: con IVA, IEPS o retenciones ObjetoImp debe ser 02.`
        }
        const exentoInvalido = trasRows.some(
          (r) => (r.tipo_factor || '') === 'Exento' && (r.impuesto || '').trim() !== '002'
        )
        if (exentoInvalido) {
          newErrors.conceptos = `Concepto ${index + 1}: TipoFactor Exento solo aplica a IVA (002).`
        }
        if (OBJETO_IMP_SIN_DESGLOSE.has(objetoImp) && (trasRows.length || retRows.length)) {
          newErrors.conceptos = `Concepto ${index + 1}: con ObjetoImp ${objetoImp} no debe capturar impuestos ni retenciones desglosados.`
        }
        if (objetoImp === '02' && !trasRows.length && !retRows.length) {
          newErrors.conceptos = `Concepto ${index + 1}: con ObjetoImp 02 debe capturar al menos un traslado (Tasa, Cuota o Exento) o una retención.`
        }
        if (retRows.length && !permiteRetenciones) {
          newErrors.conceptos = `Concepto ${index + 1}: las retenciones solo aplican cuando el emisor es persona física nacional y el receptor es persona moral nacional.`
        }
        if (!concepto.cantidad || concepto.cantidad <= 0) {
          newErrors.conceptos = `La cantidad del concepto ${index + 1} debe ser mayor a 0`
        }
        if (!concepto.precio_unitario || concepto.precio_unitario <= 0) {
          newErrors.conceptos = `El precio unitario del concepto ${index + 1} debe ser mayor a 0`
        }
        // Validar que la clave SAT sea válida si está presente
        if (concepto.clave_producto_servicio && validacionClave[index] && !validacionClave[index].valida) {
          newErrors.conceptos = `La clave SAT del concepto ${index + 1} no es válida: ${validacionClave[index].error}`
        }
      })
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      // Mostrar primer error como toast
      const firstError = Object.values(newErrors)[0]
      toast.error(firstError)
      return
    }
    
    // Preparar datos para enviar
    const submitData = {
      ...formData,
      configuracion: formData.configuracion || null, // Enviar null si no hay configuración
      // Datos del receptor
      receptor_cliente: receptorClienteId || null,
      receptor_rfc: !receptorClienteId ? receptorData.rfc : '',
      receptor_razon_social: !receptorClienteId ? receptorData.razon_social : '',
      receptor_direccion: !receptorClienteId ? receptorData.direccion : '',
      receptor_codigo_postal: !receptorClienteId ? receptorData.codigo_postal : '',
      // Siempre enviar el régimen elegido en el formulario (incluye receptor del catálogo con posible corrección)
      receptor_regimen_fiscal: receptorData.regimen_fiscal || '',
      receptor_email: receptorData.email || '',
      receptor_residencia_fiscal: !receptorClienteId
        ? (receptorData.residencia_fiscal || '').trim().toUpperCase().slice(0, 3)
        : '',
      receptor_num_reg_id_trib: !receptorClienteId
        ? (receptorData.num_reg_id_trib || '').trim().slice(0, 40)
        : '',
      nombre_receptor_pdf: (formData.nombre_receptor_pdf || '').trim(),
      conceptos: conceptos.map((c, idx) => {
        const trasRows = (c.impuestos_trasladados || []).map((r) => ({
          impuesto: (r.impuesto || '').trim(),
          descripcion: IMPUESTO_LABEL_BY_CODE[(r.impuesto || '').trim()] || (r.descripcion || ''),
          tipo_factor: r.tipo_factor || 'Tasa',
          tasa: roundMoneyN(parseFloat(r.tasa) || 0, 6),
          importe: roundMoneyN(parseFloat(r.importe) || 0, decimalesInput),
        }))
        const retRows = (c.retenciones_detalle || []).map((r) => ({
          impuesto: (r.impuesto || '').trim(),
          descripcion: IMPUESTO_LABEL_BY_CODE[(r.impuesto || '').trim()] || (r.descripcion || ''),
          tipo_factor: r.tipo_factor || 'Tasa',
          tasa: roundMoneyN(parseFloat(r.tasa) || 0, 6),
          importe: roundMoneyN(parseFloat(r.importe) || 0, decimalesInput),
        }))
        const totalTras = roundMoneyN(trasRows.reduce((s, r) => s + (parseFloat(r.importe) || 0), 0), decimalesInput)
        const totalRet = roundMoneyN(retRows.reduce((s, r) => s + (parseFloat(r.importe) || 0), 0), decimalesInput)
        const conceptoData = {
          cantidad: parseFloat(c.cantidad) || 0,
          no_identificacion: (c.no_identificacion || '').trim(),
          clave_unidad: (c.clave_unidad || '').trim(),
          unidad_medida: (c.unidad_medida || '').trim() || 'UNI',
          clave_producto_servicio: c.clave_producto_servicio || '',
          objeto_imp: (c.objeto_imp || OBJETO_IMP_DEFAULT).trim(),
          descripcion: c.descripcion || '',
          precio_unitario: roundMoneyN(parseFloat(c.precio_unitario) || 0, decimalesInput),
          importe: roundMoneyN(parseFloat(c.importe) || 0, decimalesInput),
          descuento: roundMoneyN(parseFloat(c.descuento) || 0, decimalesInput),
          iva_trasladado: totalTras,
          iva_retenido: totalRet,
          retencion_impuesto: (retRows[0]?.impuesto || (c.retencion_impuesto || '')).trim(),
          impuestos_trasladados: trasRows,
          retenciones_detalle: retRows,
          cuentas_predial: formData.tipo_comprobante_pdf === 'recibo_arrendamiento'
            ? (Array.isArray(c.cuentas_predial) ? c.cuentas_predial : [])
                .map((x) => String(x || '').trim())
                .filter(Boolean)
            : [],
          orden: c.orden || idx + 1
        }
        
        // Agregar cuenta contable asignada si existe
        if (cuentasAsignadas[idx] && cuentasAsignadas[idx].id) {
          conceptoData.cuenta_contable_id = cuentasAsignadas[idx].id
        }
        
        return conceptoData
      })
    }
    submitData.bank_account = submitData.bank_account ? parseInt(submitData.bank_account, 10) : null
    submitData.moneda = (submitData.moneda || 'MXN').toUpperCase()
    submitData.subtotal = roundMoneyN(parseFloat(submitData.subtotal) || 0, decimalesDisplay)
    submitData.tax_amount = roundMoneyN(parseFloat(submitData.tax_amount) || 0, decimalesDisplay)
    submitData.total = roundMoneyN(parseFloat(submitData.total) || 0, decimalesDisplay)
    submitData.tipo_cambio =
      submitData.moneda === 'MXN' ? 1 : roundMoneyN(parseFloat(submitData.tipo_cambio) || 0, 6)
    submitData.incluir_leyendas_fiscales = submitData.incluir_leyendas_fiscales === true
    submitData.cfdis_relacionados = cfdisRelacionados.filter(r => (r.uuid || '').trim() && (r.tipo_relacion || '').trim())
    submitData.leyendas_fiscales = leyendasFiscales

    if (formData.tipo_factura !== 'complemento') {
      delete submitData.complemento_fecha_pago
      delete submitData.complemento_referencia
    } else {
      const fechaPago = (formData.complemento_fecha_pago || '').trim()
      submitData.complemento_fecha_pago = fechaPago || null
      submitData.complemento_referencia = (formData.complemento_referencia || '').trim()
    }
    
    if (invoice) {
      submitData.id = invoice.id
    }

    // Verificar si hay conceptos con Clave SAT sin cuenta que tengan mapeo disponible
    if (formData.client) {
      const conceptosSinCuentaConClave = conceptos
        .map((c, idx) => ({ ...c, index: idx }))
        .filter(c => (c.clave_producto_servicio || '').trim() && !(cuentasAsignadas[c.index] || cuentasAsignadas[String(c.index)]))

      if (conceptosSinCuentaConClave.length > 0) {
        const conMapeo = []
        for (const c of conceptosSinCuentaConClave) {
          try {
            const params = new URLSearchParams({ cliente_id: formData.client, clave_sat: c.clave_producto_servicio.trim() })
            const res = await api.get(`/financial/invoices/obtener_cuenta_mapeada/?${params}`)
            if (res.data?.cuenta_asignada) {
              conMapeo.push({
                index: c.index,
                clave_sat: c.clave_producto_servicio.trim(),
                cuenta: res.data.cuenta_asignada
              })
            }
          } catch {
            // Ignorar errores
          }
        }

        if (conMapeo.length > 0) {
          setConceptosConMapeoDisponible(conMapeo)
          setPendingSubmitData(submitData)
          setMostrarModalMapeoDisponible(true)
          return
        }
      }
    }

    if (invoice && formData.tipo_factura === 'complemento' && !invoiceToUse?.cfdi_uuid) {
      try {
        await api.patch(`/financial/invoices/${invoiceToUse.id}/actualizar-complemento-borrador/`, {
          forma_pago: formData.forma_pago,
          fecha_pago: formData.complemento_fecha_pago || null,
          referencia: formData.complemento_referencia || '',
        })
        await api.patch(`/financial/invoices/${invoiceToUse.id}/actualizar-aplicaciones-complemento/`, {
          payment_id: complementoPaymentId ? parseInt(complementoPaymentId, 10) : (invoiceToUse?.payment || undefined),
          aplicacion_ids: complementoAplicacionIds,
          parcialidades_por_aplicacion: (complementoAplicacionIds || []).map((id) => ({
            id,
            num_parcialidad: Math.max(1, parseInt(complementoParcialidadById[id] ?? 1, 10))
          }))
        })
      } catch (err) {
        const d = err.response?.data
        const msg =
          (d && typeof d === 'object' && (d.non_field_errors?.[0] || d.detail || d.error)) ||
          'Error al actualizar documentos relacionados/parcialidades del complemento'
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg))
        return
      }
    }

    onSubmit(submitData)
  }

  const handleAplicarMapeoYEnviar = () => {
    if (!pendingSubmitData) return
    const data = { ...pendingSubmitData }
    conceptosConMapeoDisponible.forEach(({ index, cuenta }) => {
      if (data.conceptos[index]) {
        data.conceptos[index] = { ...data.conceptos[index], cuenta_contable_id: cuenta.id }
      }
    })
    setMostrarModalMapeoDisponible(false)
    setConceptosConMapeoDisponible([])
    setPendingSubmitData(null)
    onSubmit(data)
  }

  const handleContinuarSinMapeo = () => {
    if (pendingSubmitData) {
      onSubmit(pendingSubmitData)
    }
    setMostrarModalMapeoDisponible(false)
    setConceptosConMapeoDisponible([])
    setPendingSubmitData(null)
  }

  const seriesDisponibles = Array.from(
    new Set((configuraciones || []).map(c => c.serie || ''))
  )

  const configuracionesFiltradas = (configuraciones || []).filter(config => {
    if (!serieSeleccionada || serieSeleccionada === '') {
      return !config.serie || config.serie === ''
    }
    return (config.serie || '') === serieSeleccionada
  })

  const handleSerieChange = (serieValue) => {
    const serieInterna = serieValue === '__SIN_SERIE__' ? '' : serieValue
    setSerieSeleccionada(serieInterna)
    const configConSerie = (configuraciones || []).find(c => {
      if (serieInterna === '') return !c.serie || c.serie === ''
      return (c.serie || '') === serieInterna
    })
    setFormData(prev => ({
      ...prev,
      configuracion: configConSerie ? configConSerie.id : ''
    }))
  }

  const handleConfiguracionChange = (configId) => {
    setFormData(prev => ({
      ...prev,
      configuracion: configId
    }))
  }

  return (
    <Form
      onSubmit={handleSubmit}
      onInvalid={(e) => {
        e.preventDefault()
        const fieldLabels = {
          uso_cfdi: 'Uso CFDI',
          client: 'Cliente (Emisor)',
          configuracion: 'Configuración de facturación',
          issue_date: 'Fecha de emisión',
          due_date: 'Fecha de vencimiento',
          global_periodicidad: 'Periodicidad (factura global)',
          global_meses: 'Mes (factura global)',
          global_anio: 'Año (factura global)',
          moneda: 'Moneda',
          tipo_cambio: 'Tipo de cambio',
          receptor_rfc: 'RFC del receptor',
          receptor_razon_social: 'Razón social del receptor',
          receptor_direccion: 'Dirección fiscal del receptor',
          receptor_codigo_postal: 'Código postal del receptor',
          receptor_residencia_fiscal: 'Residencia fiscal',
          receptor_num_reg_id_trib: 'Núm. registro identidad fiscal'
        }
        const name = e.target?.name || ''
        const label = fieldLabels[name] || name || 'campo obligatorio'
        toast.error(`Campo obligatorio no completado: ${label}. Complete este campo en la pestaña correspondiente.`)
        if (['uso_cfdi', 'moneda', 'tipo_cambio', 'client', 'configuracion', 'issue_date', 'due_date', 'global_periodicidad', 'global_meses', 'global_anio', 'receptor_residencia_fiscal', 'receptor_num_reg_id_trib'].includes(name)) {
          setActiveTab('general')
        } else if (name.startsWith('receptor_')) {
          setActiveTab('receptor')
        } else if (name.includes('concepto') || name === 'conceptos') {
          setActiveTab('conceptos')
        }
      }}
    >
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
        <Tab eventKey="general" title="Datos Generales">
          {formData.tipo_factura !== 'complemento' && (
            <Alert variant="info" className="mb-3">
              <small>
                <strong>Información:</strong> El emisor es el cliente registrado que emite la factura.
                El receptor puede ser un cliente del sistema o una persona/empresa externa.
              </small>
            </Alert>
          )}
          
          <Row className="mb-2">
            <Col md={6} />
            <Col md={6}>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fecha de Emisión *</Form.Label>
                    <Form.Control
                      type="date"
                      name="issue_date"
                      value={formData.issue_date}
                      onChange={handleChange}
                      isInvalid={!!errors.issue_date}
                      required
                      disabled={!!invoice?.cfdi_uuid}
                      min={invoice?.cfdi_uuid ? undefined : issueDateBounds.min}
                      max={invoice?.cfdi_uuid ? undefined : issueDateBounds.max}
                    />
                    {errors.issue_date && <Form.Control.Feedback type="invalid">{errors.issue_date}</Form.Control.Feedback>}
                    {!invoice?.cfdi_uuid && (
                      <Form.Text className="text-muted">
                        Puede retroceder hasta 72 horas desde la fecha y hora actuales (zona CDMX).
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Fecha de Vencimiento *</Form.Label>
                    <Form.Control
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleChange}
                      isInvalid={!!errors.due_date}
                      required
                      disabled={!!invoice?.cfdi_uuid}
                    />
                    {errors.due_date && <Form.Control.Feedback type="invalid">{errors.due_date}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Cliente (Emisor) *</Form.Label>
                <Form.Select
                  name="client"
                  value={formData.client}
                  onChange={handleChange}
                  isInvalid={!!errors.client}
                  required
                >
                  <option value="">Seleccionar cliente emisor...</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno}`}
                    </option>
                  ))}
                </Form.Select>
                {errors.client && <Form.Control.Feedback type="invalid">{errors.client}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Cliente registrado que emitirá la factura
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tipo de Factura *</Form.Label>
                <Form.Select
                  name="tipo_factura"
                  value={formData.tipo_factura}
                  onChange={handleChange}
                  required
                  isInvalid={!!errors.tipo_factura}
                >
                  <option value="normal">Factura Normal</option>
                  <option value="global">Factura Global</option>
                </Form.Select>
                {errors.tipo_factura && <Form.Control.Feedback type="invalid">{errors.tipo_factura}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>

          {esComplemento && invoice && (
            <Alert variant="info" className="mb-3 py-2">
              <small>
                Complemento de pago en borrador. Ajuste parcialidad abajo; timbre desde el detalle.
                Para crear uno nuevo use el botón &quot;Compl. pago&quot; en la factura PPD.
              </small>
            </Alert>
          )}


          {formData.tipo_factura === 'complemento' && invoice && (
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Datos del pago (complemento pagos20)</Form.Label>
                {!invoiceToUse?.cfdi_uuid && (
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Forma de pago *</Form.Label>
                        <Form.Select
                          name="forma_pago"
                          value={formData.forma_pago || '99'}
                          onChange={handleChange}
                        >
                          <option value="01">01 - Efectivo</option>
                          <option value="03">03 - Transferencia</option>
                          <option value="04">04 - Tarjeta de crédito</option>
                          <option value="28">28 - Tarjeta de débito</option>
                          <option value="99">99 - Por definir</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Fecha de pago</Form.Label>
                        <Form.Control
                          type="date"
                          name="complemento_fecha_pago"
                          value={formData.complemento_fecha_pago || ''}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Referencia</Form.Label>
                        <Form.Control
                          type="text"
                          name="complemento_referencia"
                          value={formData.complemento_referencia || ''}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Form.Label>Documentos relacionados y parcialidad</Form.Label>
                {!invoiceToUse?.cfdi_uuid && (
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Pago</Form.Label>
                        <Form.Select
                          value={complementoPaymentId}
                          onChange={(e) => {
                            const pid = e.target.value
                            setComplementoPaymentId(pid)
                            setComplementoAplicacionIds([])
                            setComplementoParcialidadById({})
                          }}
                        >
                          <option value="">Seleccionar pago...</option>
                          {pagosParaComplemento?.map((item) => (
                            <option key={item.payment.id} value={item.payment.id}>
                              #{item.payment.id} - {item.payment.payment_date} - ${parseFloat(item.payment.amount || 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                {!invoiceToUse?.cfdi_uuid && loadingAplicacionesEditablesComplemento && (
                  <div className="text-muted small mb-2">Cargando aplicaciones editables...</div>
                )}
                {!invoiceToUse?.cfdi_uuid && !!aplicacionesEditablesComplementoError && (
                  <Alert variant="warning" className="py-2">
                    <small>
                      No fue posible cargar aplicaciones editables automáticamente. Mostrando aplicaciones vinculadas actuales.
                    </small>
                  </Alert>
                )}
                <Table size="sm" bordered hover>
                  <thead>
                    <tr>
                      {!invoiceToUse?.cfdi_uuid && <th style={{ width: 40 }}>Incluir</th>}
                      <th>Documento</th>
                      <th>Monto</th>
                      <th style={{ width: 120 }}>Parcialidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const selectedPago = complementoPaymentId
                        ? pagosParaComplemento?.find((p) => String(p.payment.id) === String(complementoPaymentId))
                        : null
                      const rows = !invoiceToUse?.cfdi_uuid
                        ? (
                            selectedPago?.aplicaciones_disponibles
                            || (
                              (aplicacionesEditablesComplemento?.rows && aplicacionesEditablesComplemento.rows.length > 0)
                                ? aplicacionesEditablesComplemento.rows
                                : (invoiceToUse?.aplicaciones_documentadas || []).map((x) => ({ ...x, selected: true }))
                            )
                          )
                        : (invoiceToUse?.aplicaciones_documentadas || [])
                      return (rows || []).map((a) => {
                      const rowId = a.id
                      const checked = invoiceToUse?.cfdi_uuid ? true : complementoAplicacionIds.includes(rowId)
                      return (
                        <tr key={rowId}>
                          {!invoiceToUse?.cfdi_uuid && (
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setComplementoAplicacionIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]))
                                    const sug = a.num_parcialidad_sugerida != null ? Number(a.num_parcialidad_sugerida) : 1
                                    setComplementoParcialidadById((prev) => ({
                                      ...prev,
                                      [rowId]: Number.isFinite(prev[rowId]) ? prev[rowId] : sug
                                    }))
                                  } else {
                                    setComplementoAplicacionIds((prev) => prev.filter((id) => id !== rowId))
                                  }
                                }}
                              />
                            </td>
                          )}
                          <td>{a.documento}</td>
                          <td>${parseFloat(a.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}</td>
                          <td>
                            <Form.Control
                              type="number"
                              min={1}
                              step={1}
                              disabled={!!invoiceToUse?.cfdi_uuid || !checked}
                              value={complementoParcialidadById[rowId] ?? a.num_parcialidad ?? 1}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10)
                                setComplementoParcialidadById((prev) => ({
                                  ...prev,
                                  [rowId]: Number.isFinite(v) && v >= 1 ? v : 1
                                }))
                              }}
                              style={{ maxWidth: '100px' }}
                            />
                          </td>
                        </tr>
                      )
                    })
                    })()}
                  </tbody>
                </Table>
                {(!invoiceToUse?.cfdi_uuid && (
                  (
                    complementoPaymentId
                      ? !(
                          pagosParaComplemento?.find((p) => String(p.payment.id) === String(complementoPaymentId))
                            ?.aplicaciones_disponibles?.length > 0
                        )
                      : (!aplicacionesEditablesComplemento?.rows || aplicacionesEditablesComplemento.rows.length === 0)
                  )
                )) && (
                  <div className="text-muted small">Sin aplicaciones disponibles para este complemento.</div>
                )}
                {!!errors.complemento_aplicaciones && <div className="text-danger small">{errors.complemento_aplicaciones}</div>}
                {!!invoiceToUse?.cfdi_uuid && (
                  <Form.Text className="text-muted">La parcialidad no se puede editar tras timbrar el complemento.</Form.Text>
                )}
              </Col>
            </Row>
          )}

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Nombre Comercial (PDF)</Form.Label>
                <Form.Control
                  type="text"
                  name="titulo_pdf"
                  value={formData.titulo_pdf || ''}
                  onChange={handleChange}
                  placeholder="Título central del PDF"
                />
                <Form.Text className="text-muted">Prioridad: valor del cliente. Editable desde factura y se sincroniza al guardar.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Tipo de Comprobante (solo PDF)</Form.Label>
                <Form.Select
                  name="tipo_comprobante_pdf"
                  value={formData.tipo_comprobante_pdf || 'factura'}
                  onChange={handleChange}
                >
                  <option value="factura">Factura</option>
                  <option value="recibo_honorarios">Recibo de honorarios</option>
                  <option value="recibo_arrendamiento">Recibo de arrendamiento</option>
                  <option value="complemento_pago">Complemento de pago</option>
                </Form.Select>
                <Form.Text className="text-muted">Etiqueta en el PDF. No se envía en el XML.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Régimen Fiscal Emisor</Form.Label>
                <Form.Select
                  name="emisor_regimen_fiscal"
                  value={formData.emisor_regimen_fiscal || ''}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar régimen fiscal...</option>
                  {regimenesFiscales?.map(regimen => (
                    <option key={regimen.id} value={regimen.id}>
                      {regimen.codigo} - {regimen.descripcion}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">Se preselecciona del emisor. Usado en XML/PDF.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Serie</Form.Label>
                    <Form.Select
                      name="serie"
                      value={serieSeleccionada === '' && seriesDisponibles.includes('') ? '__SIN_SERIE__' : serieSeleccionada}
                      onChange={(e) => handleSerieChange(e.target.value)}
                      isInvalid={!!errors.serie}
                    >
                      <option value="">Seleccionar serie...</option>
                      {seriesDisponibles.map((serie, index) => (
                        <option key={`${serie || 'sin-serie'}-${index}`} value={serie || '__SIN_SERIE__'}>
                          {serie ? `Serie ${serie}` : 'Sin serie'}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.serie && <Form.Control.Feedback type="invalid">{errors.serie}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Folio</Form.Label>
                    <Form.Select
                      name="configuracion"
                      value={formData.configuracion || ''}
                      onChange={(e) => handleConfiguracionChange(e.target.value)}
                      isInvalid={!!errors.configuracion}
                    >
                      <option value="">Seleccionar folio...</option>
                      {configuracionesFiltradas.map((config) => {
                        const invNum =
                          currentInvoiceNumber || formData.invoice_number || invoiceToUse?.invoice_number || ''
                        const invCfgId =
                          typeof invoiceToUse?.configuracion === 'object' && invoiceToUse?.configuracion?.id != null
                            ? invoiceToUse.configuracion.id
                            : (invoiceToUse?.configuracion ?? invoiceToUse?.configuracion_id)
                        const isEditingThisConfig =
                          !!invoiceToUse?.id && Number(config.id) === Number(invCfgId)
                        let folioText
                        if (isEditingThisConfig && invNum && String(invNum).trim() !== '') {
                          const fm = String(invNum).match(/-(\d+)$/)
                          folioText = fm ? fm[1] : (String(invNum).match(/(\d+)$/)?.[1] || String(invNum))
                        } else if (config.folio_actual != null) {
                          folioText = String((parseInt(config.folio_actual, 10) || 0) + 1).padStart(6, '0')
                        } else {
                          folioText = String(config.id)
                        }
                        return (
                          <option key={config.id} value={config.id}>
                            {`Folio ${folioText}`}
                            {config.serie ? ` (Serie ${config.serie})` : ' (Sin serie)'}
                          </option>
                        )
                      })}
                    </Form.Select>
                    {errors.configuracion && <Form.Control.Feedback type="invalid">{errors.configuracion}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
              </Row>
            </Col>
          </Row>
          {!esComplemento && (
          <>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Método de pago</Form.Label>
                <Form.Select name="metodo_pago" value={formData.metodo_pago || 'PUE'} onChange={handleChange}>
                  <option value="PUE">PUE - Pago en una sola exhibición</option>
                  <option value="PPD">PPD - Pago en parcialidades o diferido</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Forma de pago</Form.Label>
                <Form.Select
                  name="forma_pago"
                  value={formData.forma_pago || (formData.metodo_pago === 'PPD' ? '99' : '03')}
                  onChange={handleChange}
                  disabled={formData.metodo_pago === 'PPD'}
                >
                  {FORMA_PAGO_SAT_OPTIONS
                    .filter((opt) => formData.metodo_pago === 'PPD' || opt.value !== '99')
                    .map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </Form.Select>
                {formData.metodo_pago === 'PPD' && (
                  <Form.Text className="text-muted">
                    Con PPD la forma de pago debe ser 99 - Por definir (SAT).
                  </Form.Text>
                )}
                {formData.metodo_pago === 'PUE' && (
                  <Form.Text className="text-muted">
                    Con PUE la forma de pago no puede ser 99. Use PPD si el pago es diferido.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo de comprobante (SAT) *</Form.Label>
                <Form.Select
                  name="tipo_comprobante"
                  value={formData.tipo_comprobante || 'I'}
                  onChange={handleChange}
                  disabled={!!invoice?.cfdi_uuid || formData.tipo_factura === 'complemento'}
                >
                  <option value="I">I - Ingreso</option>
                  <option value="E">E - Egreso (Nota de crédito)</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {(formData.tipo_comprobante || 'I') === 'E'
                    ? 'Debe indicar factura(s) relacionada(s) abajo (puede ser más de una).'
                    : 'Ingreso normal. Use E para nota de crédito.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Uso CFDI *</Form.Label>
                <Form.Select
                  name="uso_cfdi"
                  value={formData.uso_cfdi || ''}
                  onChange={handleChange}
                  isInvalid={!!errors.uso_cfdi}
                  required
                >
                  <option value="">Seleccionar uso CFDI...</option>
                  <option value="G01">G01 - Adquisición de mercancías</option>
                  <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                  <option value="G03">G03 - Gastos en general</option>
                  <option value="I01">I01 - Construcciones</option>
                  <option value="I02">I02 - Mobiliario y equipo de oficina por inversiones</option>
                  <option value="I03">I03 - Equipo de transporte</option>
                  <option value="I04">I04 - Equipo de computo y accesorios</option>
                  <option value="I05">I05 - Dados, troqueles, moldes, matrices y herramental</option>
                  <option value="I06">I06 - Comunicaciones telefónicas</option>
                  <option value="I07">I07 - Comunicaciones satelitales</option>
                  <option value="I08">I08 - Otra maquinaria y equipo</option>
                  <option value="D01">D01 - Honorarios médicos, dentales y gastos hospitalarios</option>
                  <option value="D02">D02 - Gastos médicos por incapacidad o discapacidad</option>
                  <option value="D03">D03 - Gastos funerales</option>
                  <option value="D04">D04 - Donativos</option>
                  <option value="D05">D05 - Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)</option>
                  <option value="D06">D06 - Aportaciones voluntarias al SAR</option>
                  <option value="D07">D07 - Primas por seguros de gastos médicos</option>
                  <option value="D08">D08 - Gastos de transportación escolar obligatoria</option>
                  <option value="D09">D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones</option>
                  <option value="D10">D10 - Pagos por servicios educativos (colegiaturas)</option>
                  <option value="S01">S01 - Sin efectos fiscales</option>
                  <option value="CP01">CP01 - Pagos</option>
                  <option value="CN01">CN01 - Nómina</option>
                </Form.Select>
                {errors.uso_cfdi && <Form.Control.Feedback type="invalid">{errors.uso_cfdi}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Uso del comprobante fiscal según el catálogo del SAT
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          </>
          )}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Exportación (SAT)</Form.Label>
                <Form.Select
                  name="exportacion"
                  value={formData.exportacion || '01'}
                  onChange={handleChange}
                >
                  <option value="01">01 - No aplica</option>
                  <option value="02">02 - Definitiva con clave A1</option>
                  <option value="03">03 - Temporal</option>
                  <option value="04">04 - Definitiva distinta a A1 / sin enajenación CFF</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Cuenta bancaria transacción</Form.Label>
                <Form.Select
                  name="bank_account"
                  value={formData.bank_account || ''}
                  onChange={handleChange}
                >
                  <option value="">Seleccionar...</option>
                  {(bankAccounts || []).map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name} {acc.account_number}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Se usa para uso CFDI.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Condiciones de pago</Form.Label>
                <Form.Control
                  type="text"
                  name="condiciones_pago"
                  value={formData.condiciones_pago || ''}
                  onChange={handleChange}
                  placeholder="Ej: Nombre cuenta, número - fecha"
                  maxLength={1000}
                  disabled={!!invoice?.cfdi_uuid}
                />
                <Form.Text className="text-muted">
                  Texto libre. Se actualiza al elegir cuenta bancaria. Máx. 1000 caracteres.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={2}>
              <Form.Group>
                <Form.Label>Moneda</Form.Label>
                <Form.Select
                  name="moneda"
                  value={formData.moneda || 'MXN'}
                  onChange={handleChange}
                  isInvalid={!!errors.moneda}
                >
                  <option value="">Seleccionar moneda...</option>
                  <option value="MXN">MXN - Peso Mexicano (2 dec)</option>
                  {(catalogoMonedas || []).filter((m) => m.codigo !== 'MXN').map((m) => (
                    <option key={m.codigo} value={m.codigo}>
                      {m.codigo} - {m.nombre} ({m.decimales} dec)
                    </option>
                  ))}
                </Form.Select>
                {errors.moneda && <Form.Control.Feedback type="invalid">{errors.moneda}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Tipo de Cambio</Form.Label>
                <Form.Control
                  type="number"
                  step="any"
                  min="0"
                  name="tipo_cambio"
                  value={(formData.moneda || 'MXN') === 'MXN' ? '1' : (formData.tipo_cambio || '')}
                  onChange={handleChange}
                  isInvalid={!!errors.tipo_cambio}
                  disabled={(formData.moneda || 'MXN') === 'MXN'}
                />
                {errors.tipo_cambio && <Form.Control.Feedback type="invalid">{errors.tipo_cambio}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Para MXN se fija en 1.
                </Form.Text>
              </Form.Group>
            </Col>
            {(!esComplemento && (
              formData.tipo_factura === 'global' ||
              (tipoReceptor === 'publico_nacional' || ((receptorData.rfc || '').toUpperCase().trim() === 'XAXX010101000'))
            )) && (
              <>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Periodicidad (SAT) *</Form.Label>
                    <Form.Select
                      name="global_periodicidad"
                      value={formData.global_periodicidad || ''}
                      onChange={handleChange}
                      required
                      isInvalid={!!errors.global_periodicidad}
                    >
                      <option value="">Seleccionar...</option>
                      {PERIODICIDAD_SAT.map((p) => (
                        <option key={p.codigo} value={p.codigo}>
                          {p.codigo} — {p.descripcion}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.global_periodicidad && (
                      <Form.Control.Feedback type="invalid">{errors.global_periodicidad}</Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Mes (SAT) *</Form.Label>
                    <Form.Select
                      name="global_meses"
                      value={formData.global_meses || ''}
                      onChange={handleChange}
                      required
                      isInvalid={!!errors.global_meses}
                    >
                      <option value="">Seleccionar...</option>
                      {MESES_SAT.map((m) => (
                        <option key={m.codigo} value={m.codigo}>
                          {m.codigo} — {m.descripcion}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.global_meses && (
                      <Form.Control.Feedback type="invalid">{errors.global_meses}</Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Año (SAT) *</Form.Label>
                    <Form.Control
                      type="number"
                      name="global_anio"
                      min={2000}
                      max={2100}
                      value={formData.global_anio ?? ''}
                      onChange={handleChange}
                      isInvalid={!!errors.global_anio}
                      placeholder="2024"
                    />
                    {errors.global_anio && (
                      <Form.Control.Feedback type="invalid">{errors.global_anio}</Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </>
            )}
            {!((!esComplemento && (
              formData.tipo_factura === 'global' ||
              (tipoReceptor === 'publico_nacional' || ((receptorData.rfc || '').toUpperCase().trim() === 'XAXX010101000'))
            ))) && <Col md={8} />}
          </Row>

          {/* Facturas relacionadas (obligatorio en Tipo E; puede ser varias) */}
          <Row className="mb-3">
            <Col md={12}>
              <Form.Label>
                Facturas relacionadas
                {(formData.tipo_comprobante || 'I') === 'E' ? ' *' : ''}
              </Form.Label>
              {errors.cfdis_relacionados && (
                <Alert variant="danger" className="py-1 px-2">{errors.cfdis_relacionados}</Alert>
              )}
              <div className="mb-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setCfdisRelacionados([...cfdisRelacionados, { uuid: '', tipo_relacion: '01' }])}>
                  + Agregar factura relacionada
                </Button>
              </div>
              {cfdisRelacionados.length > 0 && (
                <ListGroup>
                  {cfdisRelacionados.map((rel, idx) => (
                    <ListGroup.Item key={idx} className="d-flex align-items-center gap-2 flex-wrap">
                      <Form.Control
                        type="text"
                        placeholder="UUID (ej: A39DA66B-52CA-49E3-879B-5C05185B0EF7)"
                        value={rel.uuid || ''}
                        onChange={(e) => {
                          const arr = [...cfdisRelacionados]
                          arr[idx] = { ...arr[idx], uuid: e.target.value }
                          setCfdisRelacionados(arr)
                        }}
                        size="sm"
                        style={{ maxWidth: '320px' }}
                      />
                      <Form.Select
                        size="sm"
                        value={rel.tipo_relacion || '01'}
                        onChange={(e) => {
                          const arr = [...cfdisRelacionados]
                          arr[idx] = { ...arr[idx], tipo_relacion: e.target.value }
                          setCfdisRelacionados(arr)
                        }}
                        style={{ width: 'auto', minWidth: '200px' }}
                      >
                        <option value="01">01 - Nota de crédito</option>
                        <option value="02">02 - Nota de débito</option>
                        <option value="03">03 - Devolución de mercancía</option>
                        <option value="04">04 - Sustitución de CFDI previos</option>
                        {(formData.tipo_comprobante || 'I') !== 'E' && (
                          <>
                            <option value="05">05 - Traslados facturados previamente</option>
                            <option value="06">06 - Factura por traslados previos</option>
                          </>
                        )}
                        <option value="07">07 - CFDI por aplicación de anticipo</option>
                        {(formData.tipo_comprobante || 'I') !== 'E' && (
                          <>
                            <option value="08">08 - Factura generada por pagos en parcialidades</option>
                            <option value="09">09 - Factura generada por pagos diferidos</option>
                          </>
                        )}
                      </Form.Select>
                      <Button variant="outline-danger" size="sm" onClick={() => setCfdisRelacionados(cfdisRelacionados.filter((_, i) => i !== idx))}>
                        Eliminar
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              <Form.Text className="text-muted">
                {(formData.tipo_comprobante || 'I') === 'E'
                  ? 'Nota de crédito: indique uno o más UUID de facturas de ingreso (mismo receptor, no canceladas). Tipo relación por defecto 01.'
                  : 'UUID de facturas previas relacionadas (opcional en ingreso).'}
              </Form.Text>
            </Col>
          </Row>

          {/* Leyendas fiscales */}
          <Row className="mb-3">
            <Col md={12}>
              <div className="d-flex align-items-center gap-3 mb-2">
                <Form.Label className="mb-0">Leyendas fiscales</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="incluir_leyendas_fiscales"
                  name="incluir_leyendas_fiscales"
                  label="Incluir leyendas fiscales en XML y PDF"
                  checked={formData.incluir_leyendas_fiscales === true}
                  onChange={(e) => setFormData(prev => ({ ...prev, incluir_leyendas_fiscales: e.target.checked }))}
                  disabled={!!invoice?.cfdi_uuid}
                />
              </div>
              <div className="mb-2">
                <Button variant="outline-secondary" size="sm" onClick={() => setLeyendasFiscales([...leyendasFiscales, { disposicionFiscal: '', norma: '', textoLeyenda: '' }])}>
                  + Agregar leyenda
                </Button>
              </div>
              {leyendasFiscales.length > 0 && (
                <ListGroup>
                  {leyendasFiscales.map((ley, idx) => (
                    <ListGroup.Item key={idx} className="d-flex align-items-start gap-2 flex-wrap">
                      <Form.Control
                        type="text"
                        placeholder="Disposición fiscal (ej: ISR)"
                        value={ley.disposicionFiscal || ''}
                        onChange={(e) => {
                          const arr = [...leyendasFiscales]
                          arr[idx] = { ...arr[idx], disposicionFiscal: e.target.value }
                          setLeyendasFiscales(arr)
                        }}
                        size="sm"
                        style={{ minWidth: '100px' }}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Norma (ej: 133)"
                        value={ley.norma || ''}
                        onChange={(e) => {
                          const arr = [...leyendasFiscales]
                          arr[idx] = { ...arr[idx], norma: e.target.value }
                          setLeyendasFiscales(arr)
                        }}
                        size="sm"
                        style={{ minWidth: '80px' }}
                      />
                      <Form.Control
                        type="text"
                        placeholder="Texto de leyenda"
                        value={ley.textoLeyenda || ''}
                        onChange={(e) => {
                          const arr = [...leyendasFiscales]
                          arr[idx] = { ...arr[idx], textoLeyenda: e.target.value }
                          setLeyendasFiscales(arr)
                        }}
                        size="sm"
                        style={{ flex: 1, minWidth: '150px' }}
                      />
                      <Button variant="outline-danger" size="sm" onClick={() => setLeyendasFiscales(leyendasFiscales.filter((_, i) => i !== idx))}>
                        Eliminar
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
              <Form.Text className="text-muted">Se incluyen en XML/PDF solo si el checkbox está marcado.</Form.Text>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  name="observaciones"
                  value={formData.observaciones || ''}
                  onChange={handleChange}
                  placeholder="Observaciones (opcional, solo para PDF)"
                  maxLength={500}
                  rows={3}
                  disabled={!!invoice?.cfdi_uuid}
                />
                <Form.Text className="text-muted">
                  Solo se muestra en el PDF si tiene valor. Máx. 500 caracteres.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Información de Configuración PAC */}
          {formData.client && configuracionPACPredeterminada && (
            <Row className="mb-3">
              <Col md={12}>
                <div className="p-3 bg-light rounded">
                  <h6 className="mb-2">Configuración PAC (Timbrado)</h6>
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                    <div className="small">
                      <div><strong>PAC:</strong> {configuracionPACPredeterminada.nombre} ({configuracionPACPredeterminada.tipo_pac})</div>
                      <div><strong>Ambiente:</strong> {configuracionPACPredeterminada.ambiente === 'produccion' ? 'Producción' : 'Pruebas'}</div>
                      {configuracionPACPredeterminada.certificados_configurados === true && (
                        <div className="text-success"><strong>✓ Certificado CSD y Llave Privada configurados</strong></div>
                      )}
                      {configuracionPACPredeterminada.certificados_configurados !== true && (
                        <div className="text-warning">
                          <small><strong>Advertencia:</strong> Configuración PAC incompleta. Configure los certificados para poder timbrar facturas.</small>
                        </div>
                      )}
                    </div>
                    <Form.Check
                      type="checkbox"
                      id="desglose-impuestos-pdf-form"
                      className="ms-auto"
                      label="Desglose de impuestos"
                      checked={!!formData.desglose_impuestos_pdf}
                      disabled={!!invoice?.cfdi_uuid}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        desglose_impuestos_pdf: e.target.checked,
                      }))}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {formData.client && !configuracionPACPredeterminada && (
            <Row className="mb-3">
              <Col md={12} className="d-flex justify-content-end">
                <Form.Check
                  type="checkbox"
                  id="desglose-impuestos-pdf-form-sin-pac"
                  label="Desglose de impuestos"
                  checked={!!formData.desglose_impuestos_pdf}
                  disabled={!!invoice?.cfdi_uuid}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    desglose_impuestos_pdf: e.target.checked,
                  }))}
                />
              </Col>
            </Row>
          )}
          
          {formData.client && (!configuracionesPAC || configuracionesPAC.length === 0) && (
            <Row className="mb-3">
              <Col md={12}>
                <Alert variant="warning" className="py-2 mb-0">
                  <small>
                    <strong>Advertencia:</strong> Este cliente no tiene configuraciones PAC activas. 
                    Configure una configuración PAC para poder timbrar facturas.
                  </small>
                </Alert>
              </Col>
            </Row>
          )}

          

          {formData.client && configuraciones && configuraciones.length > 0 && (
            <>
              {configuracionSeleccionada && (
                <Row className="mb-3">
                  <Col md={12}>
                    <div className="p-3 bg-light rounded">
                      <h6 className="mb-2">Información de la Configuración</h6>
                      <div className="small">
                        <div><strong>Serie:</strong> {configuracionSeleccionada.serie || 'Sin serie'}</div>
                        {(() => {
                          const isEditing = !!invoiceToUse?.id
                          const invoiceNumber =
                            currentInvoiceNumber ||
                            formData.invoice_number ||
                            invoiceToUse?.invoice_number ||
                            (invoice && invoice.invoice_number) ||
                            ''
                          if (isEditing) {
                            if (invoiceNumber && String(invoiceNumber).trim() !== '') {
                              const folioMatch = String(invoiceNumber).match(/-(\d+)$/)
                              if (folioMatch) {
                                return <div><strong>Folio actual:</strong> {folioMatch[1]}</div>
                              }
                              const numeroMatch = String(invoiceNumber).match(/(\d+)$/)
                              if (numeroMatch) {
                                return <div><strong>Folio actual:</strong> {numeroMatch[1]}</div>
                              }
                              return <div><strong>Folio actual:</strong> {invoiceNumber}</div>
                            }
                            return <div><strong>Folio actual:</strong> —</div>
                          }
                          if (configuracionSeleccionada.folio_actual != null) {
                            return (
                              <div>
                                <strong>Folio siguiente:</strong>{' '}
                                {String((parseInt(configuracionSeleccionada.folio_actual, 10) || 0) + 1).padStart(6, '0')}
                              </div>
                            )
                          }
                          return <div><strong>Folio siguiente:</strong> —</div>
                        })()}
                        {configuracionSeleccionada.monto_maximo && (
                          <div><strong>Monto máximo:</strong> ${parseFloat(configuracionSeleccionada.monto_maximo).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}</div>
                        )}
                        {configuracionSeleccionada.tipos_permitidos && configuracionSeleccionada.tipos_permitidos.length > 0 && (
                          <div>
                            <strong>Tipos permitidos:</strong>{' '}
                            {configuracionSeleccionada.tipos_permitidos.map(tipo => {
                              const tiposMap = { normal: 'Normal', global: 'Global', complemento: 'Complemento' }
                              return tiposMap[tipo] || tipo
                            }).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              
              {/* Filtrar tipos de factura según configuración */}
              {configuracionSeleccionada && configuracionSeleccionada.tipos_permitidos && configuracionSeleccionada.tipos_permitidos.length > 0 && (
                <Row className="mb-3">
                  <Col md={12}>
                    <Alert variant="info" className="py-2 mb-0">
                      <small>
                        <strong>Nota:</strong> Solo se permiten los siguientes tipos de factura para esta configuración:{' '}
                        {configuracionSeleccionada.tipos_permitidos.map(tipo => {
                          const tiposMap = { normal: 'Normal', global: 'Global', complemento: 'Complemento' }
                          return tiposMap[tipo] || tipo
                        }).join(', ')}
                      </small>
                    </Alert>
                  </Col>
                </Row>
              )}
            </>
          )}
          
          {formData.client && (!configuraciones || configuraciones.length === 0) && (
            <Row className="mb-3">
              <Col md={12}>
                <Alert variant="warning" className="py-2 mb-0">
                  <small>
                    <strong>Advertencia:</strong> Este cliente no tiene configuraciones de facturación activas. 
                    Se usará la configuración predeterminada.
                  </small>
                </Alert>
              </Col>
            </Row>
          )}
        </Tab>
        
        <Tab eventKey="receptor" title="Receptor">
          <Alert variant="info" className="mb-3">
            <small>
              <strong>Información:</strong> El receptor puede ser un cliente registrado en el sistema o una persona/empresa externa. 
              Si el receptor está en el sistema, selecciónelo para autocompletar los datos. Si no, déjelo en blanco e ingrese los datos manualmente.
              También puede seleccionar Público en General (Nacional o Extranjero) para usar los datos del emisor.
            </small>
          </Alert>

          {!!invoice?.cfdi_uuid && (
            <Alert variant="secondary" className="mb-3 py-2">
              <small>La factura ya está timbrada. Los datos del receptor no pueden modificarse.</small>
            </Alert>
          )}
          {formData.tipo_factura === 'global' && !invoice?.cfdi_uuid && (
            <Alert variant="info" className="mb-3 py-2">
              <small>Factura Global: Se autocompleta el receptor con datos del emisor. Puede modificar los datos o usar Público Nacional/Extranjero.</small>
            </Alert>
          )}
          
          <Row className="mb-3">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Receptor (Cliente del Sistema) - Opcional</Form.Label>
                <Form.Select
                  name="receptor_cliente"
                  value={receptorClienteId}
                  onChange={handleChange}
                  isInvalid={!!errors.receptor_cliente}
                  disabled={!!invoice?.cfdi_uuid || tipoReceptor !== 'seleccionado'}
                >
                  <option value="">Ninguno (Ingresar datos manualmente)</option>
                  {clients?.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno}`}
                    </option>
                  ))}
                </Form.Select>
                {errors.receptor_cliente && <Form.Control.Feedback type="invalid">{errors.receptor_cliente}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Si el receptor es un cliente registrado, selecciónelo aquí. Si es externo, seleccione &quot;Ninguno&quot; e ingrese los datos a continuación.
                </Form.Text>
              </Form.Group>
            </Col>
            {!invoice?.cfdi_uuid && (
              <Col md={4} className="d-flex flex-column justify-content-end pb-2">
                <Form.Label className="small text-muted">&nbsp;</Form.Label>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant={tipoReceptor === 'seleccionado' ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => {
                      setTipoReceptor('seleccionado')
                      setReceptorClienteId('')
                      setReceptorEsExterno(true)
                      setReceptorData({ rfc: '', razon_social: '', direccion: '', codigo_postal: '', regimen_fiscal: '', email: '', residencia_fiscal: '', num_reg_id_trib: '' })
                      const emisorUso = getUsoCfdiEmisor(clients, formData.client)
                      setFormData((prev) => ({
                        ...prev,
                        uso_cfdi: resolveUsoCfdiPorTipoFactura(prev.tipo_factura, emisorUso) || prev.uso_cfdi
                      }))
                    }}
                  >
                    Receptor
                  </Button>
                  <Button
                    variant={tipoReceptor === 'publico_nacional' ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={handlePublicoNacionalClick}
                    title={!formData.client ? 'Seleccione primero el emisor' : 'Público en General Nacional (RFC: XAXX010101000)'}
                  >
                    Público Nacional
                  </Button>
                  <Button
                    variant={tipoReceptor === 'publico_extranjero' ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={handlePublicoExtranjeroClick}
                    title={!formData.client ? 'Seleccione primero el emisor' : 'Público en General Extranjero (RFC: XEXX010101000)'}
                  >
                    Público Extranjero
                  </Button>
                </div>
              </Col>
            )}
          </Row>
          
          <hr className="my-4" />
          <h6>Datos del Receptor * {(formData.tipo_factura === 'global' || tipoReceptor === 'publico_nacional') && tipoReceptor === 'seleccionado' && <Badge bg="secondary" className="ms-2">Factura Global/Público Nacional - Receptor = Emisor</Badge>}{tipoReceptor !== 'seleccionado' && <Badge bg="info" className="ms-2">Público en General {tipoReceptor === 'publico_nacional' ? 'Nacional' : 'Extranjero'}</Badge>}</h6>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>RFC *</Form.Label>
                <Form.Control
                  type="text"
                  name="receptor_rfc"
                  value={receptorData.rfc}
                  onChange={handleChange}
                  placeholder="RFC del receptor"
                  maxLength={13}
                  isInvalid={!!errors.receptor_rfc}
                  required={!receptorClienteId}
                  disabled={!!invoice?.cfdi_uuid || (formData.tipo_factura !== 'global' && tipoReceptor !== 'seleccionado')}
                />
                {errors.receptor_rfc && <Form.Control.Feedback type="invalid">{errors.receptor_rfc}</Form.Control.Feedback>}
              </Form.Group>
              {tipoReceptor === 'publico_extranjero' && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Residencia Fiscal *</Form.Label>
                    <Form.Select
                      name="receptor_residencia_fiscal"
                      value={receptorData.residencia_fiscal || ''}
                      onChange={handleChange}
                      isInvalid={!!errors.receptor_residencia_fiscal}
                      disabled={!!invoice?.cfdi_uuid}
                    >
                      <option value="">Seleccionar país (c_Pais)...</option>
                      {(catalogoPaises || []).map((p) => (
                        <option key={p.codigo} value={p.codigo}>
                          {p.codigo} — {p.nombre}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.receptor_residencia_fiscal && (
                      <Form.Control.Feedback type="invalid">{errors.receptor_residencia_fiscal}</Form.Control.Feedback>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Número de Identificación Fiscal *</Form.Label>
                    <Form.Control
                      type="text"
                      name="receptor_num_reg_id_trib"
                      value={receptorData.num_reg_id_trib || ''}
                      onChange={handleChange}
                      placeholder="Identificación fiscal en el país de residencia"
                      maxLength={40}
                      isInvalid={!!errors.receptor_num_reg_id_trib}
                      disabled={!!invoice?.cfdi_uuid}
                    />
                    {errors.receptor_num_reg_id_trib && (
                      <Form.Control.Feedback type="invalid">{errors.receptor_num_reg_id_trib}</Form.Control.Feedback>
                    )}
                  </Form.Group>
                </>
              )}
            </Col>
            <Col md={6}>
              <Form.Group className={(tipoReceptor === 'publico_nacional' || tipoReceptor === 'publico_extranjero') ? 'mb-3' : ''}>
                <Form.Label>Razón Social *</Form.Label>
                <Form.Control
                  type="text"
                  name="receptor_razon_social"
                  value={receptorData.razon_social}
                  onChange={handleChange}
                  placeholder="Nombre o razón social del receptor"
                  isInvalid={!!errors.receptor_razon_social}
                  required
                  disabled={!!invoice?.cfdi_uuid || (formData.tipo_factura !== 'global' && tipoReceptor !== 'seleccionado')}
                />
                {errors.receptor_razon_social && <Form.Control.Feedback type="invalid">{errors.receptor_razon_social}</Form.Control.Feedback>}
              </Form.Group>
              {(tipoReceptor === 'publico_nacional' || tipoReceptor === 'publico_extranjero') && (
                <Form.Group>
                  <Form.Label>Nombre Receptor</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre_receptor_pdf"
                    value={formData.nombre_receptor_pdf || ''}
                    onChange={handleChange}
                    placeholder="Opcional (solo PDF)"
                    maxLength={255}
                    disabled={!!invoice?.cfdi_uuid}
                  />
                  <Form.Text className="text-muted">
                    Solo se mostrará en el PDF debajo de &quot;Nombre receptor:&quot;.
                  </Form.Text>
                </Form.Group>
              )}
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Dirección Fiscal *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="receptor_direccion"
                  value={receptorData.direccion}
                  onChange={handleChange}
                  placeholder="Calle, número, colonia, municipio, estado, código postal, país"
                  isInvalid={!!errors.receptor_direccion}
                  required
                  disabled={!!invoice?.cfdi_uuid || (formData.tipo_factura !== 'global' && tipoReceptor !== 'seleccionado' && tipoReceptor !== 'publico_extranjero')}
                />
                {errors.receptor_direccion && <Form.Control.Feedback type="invalid">{errors.receptor_direccion}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Código Postal *</Form.Label>
                <Form.Control
                  type="text"
                  name="receptor_codigo_postal"
                  value={receptorData.codigo_postal}
                  onChange={handleChange}
                  placeholder="00000"
                  maxLength={5}
                  isInvalid={!!errors.receptor_codigo_postal}
                  required
                  disabled={
                    !!invoice?.cfdi_uuid ||
                    tipoReceptor === 'publico_extranjero' ||
                    (formData.tipo_factura !== 'global' && tipoReceptor !== 'seleccionado')
                  }
                />
                {errors.receptor_codigo_postal && <Form.Control.Feedback type="invalid">{errors.receptor_codigo_postal}</Form.Control.Feedback>}
                {tipoReceptor === 'publico_extranjero' && (
                  <Form.Text className="text-muted">Igual al código postal del emisor; se actualiza al pulsar &quot;Público Extranjero&quot;.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Régimen Fiscal Receptor</Form.Label>
                <Form.Select
                  name="receptor_regimen_fiscal"
                  value={receptorData.regimen_fiscal || ''}
                  onChange={handleChange}
                  isInvalid={!!errors.receptor_regimen_fiscal}
                  disabled={!!invoice?.cfdi_uuid}
                >
                  <option value="">Seleccionar régimen fiscal...</option>
                  {regimenesFiscales?.map(regimen => (
                    <option key={regimen.id} value={`${regimen.codigo} - ${regimen.descripcion}`}>
                      {regimen.codigo} - {regimen.descripcion}
                    </option>
                  ))}
                </Form.Select>
                {errors.receptor_regimen_fiscal && <Form.Control.Feedback type="invalid">{errors.receptor_regimen_fiscal}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Se autocompleta con el régimen del cliente seleccionado; puede cambiarlo si lo requiere.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Email(s)</Form.Label>
                {/*
                  Solo se bloquea cuando la factura YA esta timbrada. Antes tambien se
                  bloqueaba con receptor publico nacional o extranjero, y ahi es donde el
                  contador lo necesita: "al crear factura extranjero me freezea el correo
                  para configurar el envio" (01/09/2026). El correo no viaja en el XML
                  --sirve para mandarle el CFDI al receptor--, asi que no habia nada fiscal
                  que proteger.
                */}
                <Form.Control
                  type="text"
                  name="receptor_email"
                  value={receptorData.email}
                  onChange={handleChange}
                  placeholder="correo1@dominio.com, correo2@dominio.com"
                  isInvalid={!!errors.receptor_email}
                  disabled={!!invoice?.cfdi_uuid}
                />
                {errors.receptor_email && <Form.Control.Feedback type="invalid">{errors.receptor_email}</Form.Control.Feedback>}
                <Form.Text className="text-muted">
                  Puede agregar varios correos separados por coma.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="conceptos" title="Conceptos">
          {errors.conceptos && <Alert variant="danger">{errors.conceptos}</Alert>}
          
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Conceptos de Factura</h5>
            <Button variant="primary" size="sm" onClick={addConcepto}>
              + Agregar Concepto
            </Button>
          </div>

          {conceptos.length > 0 ? (
            <div style={{ position: 'relative', overflow: 'visible' }}>
              <Table responsive style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: '90px' }}>SKU</th>
                    <th style={{ minWidth: '80px' }}>Cantidad</th>
                    <th style={{ minWidth: '90px' }}>ClaveUnidad</th>
                    <th style={{ minWidth: '90px' }}>Unidad</th>
                    <th style={{ minWidth: '200px' }}>Clave SAT</th>
                    <th style={{ minWidth: '180px' }}>Descripción</th>
                    <th style={{ minWidth: '100px' }}>Precio Unit.</th>
                    <th style={{ minWidth: '90px' }}>Descuento</th>
                    <th style={{ minWidth: '90px' }}>Importe</th>
                    <th style={{ minWidth: '280px' }}>Objeto impuesto</th>
                    <th style={{ minWidth: '110px' }}>Impuestos</th>
                    <th style={{ minWidth: '110px' }}>Retenciones</th>
                    <th style={{ minWidth: '130px' }}>Cuenta Cont.</th>
                    <th style={{ minWidth: '80px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                {conceptos.map((concepto, index) => {
                  // Obtener cuenta asignada con verificación flexible
                  const cuentaAsignada = cuentasAsignadas[index] || cuentasAsignadas[String(index)]
                  return (
                  <tr key={`concepto-${index}-${cuentaAsignada?.id || 'sin-cuenta'}`}>
                    <td>
                      <Form.Control
                        type="text"
                        placeholder="opcional"
                        value={concepto.no_identificacion || ''}
                        onChange={(e) => handleConceptoChange(index, 'no_identificacion', e.target.value)}
                        size="sm"
                        disabled={!!invoice?.cfdi_uuid}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.0001"
                        value={concepto.cantidad || ''}
                        onChange={(e) => handleConceptoChange(index, 'cantidad', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        placeholder="ClaveUnidad"
                        value={concepto.clave_unidad || ''}
                        onChange={(e) => handleConceptoChange(index, 'clave_unidad', e.target.value)}
                        onFocus={(e) => onClaveUnidadFocus(index, e)}
                        onBlur={(e) => onClaveUnidadBlur(index, e)}
                        size="sm"
                        isInvalid={!(concepto.clave_unidad || '').trim()}
                        required
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        placeholder="Unidad (opcional)"
                        value={concepto.unidad_medida || ''}
                        onChange={(e) => handleConceptoChange(index, 'unidad_medida', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td style={{ minWidth: '200px' }}>
                      <InputGroup size="sm">
                        <Form.Control
                          type="text"
                          placeholder="Clave SAT..."
                          value={concepto.clave_producto_servicio || ''}
                          onChange={(e) => {
                            handleConceptoChange(index, 'clave_producto_servicio', e.target.value)
                            if (e.target.value.trim()) {
                              validarClaveSAT(index, e.target.value.trim())
                            } else {
                              setValidacionClave(prev => ({ ...prev, [index]: null }))
                            }
                          }}
                          onBlur={() => {
                            if (concepto.clave_producto_servicio && concepto.clave_producto_servicio.trim()) {
                              validarClaveSAT(index, concepto.clave_producto_servicio.trim())
                            }
                          }}
                          isInvalid={validacionClave[index] && !validacionClave[index].valida}
                          readOnly
                          style={{ cursor: 'pointer', backgroundColor: 'white' }}
                          onClick={() => abrirModalClaveSAT(index)}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => abrirModalClaveSAT(index)}
                          title="Buscar clave SAT"
                        >
                          🔍
                        </Button>
                        {validacionClave[index] && (
                          <InputGroup.Text>
                            {validacionClave[index].valida ? (
                              <Badge bg="success">✓</Badge>
                            ) : (
                              <Badge bg="danger" title={validacionClave[index].error}>✗</Badge>
                            )}
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                      {validacionClave[index] && !validacionClave[index].valida && (
                        <Form.Text className="text-danger" style={{ fontSize: '0.75rem', display: 'block' }}>
                          {validacionClave[index].error}
                        </Form.Text>
                      )}
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={concepto.descripcion || ''}
                        onChange={(e) => handleConceptoChange(index, 'descripcion', e.target.value)}
                        size="sm"
                        required
                      />
                      <div className="mt-1">
                        {formData.tipo_comprobante_pdf === 'recibo_arrendamiento' && (
                          <>
                        <Form.Text className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                          Cuenta predial (opcional)
                        </Form.Text>
                        {(Array.isArray(concepto.cuentas_predial) && concepto.cuentas_predial.length
                          ? concepto.cuentas_predial
                          : ['']
                        ).map((num, pIdx) => (
                          <InputGroup size="sm" className="mb-1" key={`predial-${index}-${pIdx}`}>
                            <Form.Control
                              type="text"
                              placeholder="Solo letras y números"
                              value={num || ''}
                              maxLength={150}
                              disabled={!!invoice?.cfdi_uuid}
                              onChange={(e) => updateCuentaPredial(index, pIdx, e.target.value)}
                            />
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={!!invoice?.cfdi_uuid}
                              onClick={() => addCuentaPredial(index)}
                              title="Agregar otra cuenta predial"
                            >
                              +
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              disabled={!!invoice?.cfdi_uuid || ((concepto.cuentas_predial || []).length <= 1 && !(num || '').trim())}
                              onClick={() => removeCuentaPredial(index, pIdx)}
                              title="Quitar"
                            >
                              ×
                            </Button>
                          </InputGroup>
                        ))}
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.0001"
                        value={concepto.precio_unitario || ''}
                        onChange={(e) => handleConceptoChange(index, 'precio_unitario', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={concepto.descuento || ''}
                        onChange={(e) => handleConceptoChange(index, 'descuento', e.target.value)}
                        size="sm"
                      />
                    </td>
                    <td>
                      ${parseFloat(concepto.importe || 0).toLocaleString('es-MX', {
                        minimumFractionDigits: decimalesDisplay,
                        maximumFractionDigits: decimalesDisplay
                      })}
                    </td>
                    <td style={{ minWidth: '280px' }}>
                      <Form.Select
                        size="sm"
                        value={concepto.objeto_imp || OBJETO_IMP_DEFAULT}
                        onChange={(e) => handleConceptoChange(index, 'objeto_imp', e.target.value)}
                        disabled={!!invoice?.cfdi_uuid}
                        title={OBJETO_IMP_SAT_OPTIONS.find((o) => o.value === (concepto.objeto_imp || OBJETO_IMP_DEFAULT))?.label}
                      >
                        {OBJETO_IMP_SAT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => openTaxModal(index, 'traslado')}
                        disabled={!!invoice?.cfdi_uuid || OBJETO_IMP_SIN_DESGLOSE.has(concepto.objeto_imp || OBJETO_IMP_DEFAULT)}
                      >
                        Impuestos (${parseFloat(concepto.iva_trasladado || 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })})
                      </Button>
                    </td>
                    <td>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => openTaxModal(index, 'retencion')}
                        disabled={!!invoice?.cfdi_uuid || !permiteRetenciones}
                        title={permiteRetenciones
                          ? 'Capturar retenciones (PF nacional → PM nacional)'
                          : 'Retenciones solo aplican PF nacional → PM nacional'}
                      >
                        Retenciones (${parseFloat(concepto.iva_retenido || 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })})
                      </Button>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {cuentaAsignada && cuentaAsignada.id && cuentaAsignada.code ? (
                          <>
                            <Badge bg="success" title={cuentaAsignada.name || cuentaAsignada.code}>
                              {cuentaAsignada.code}
                            </Badge>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => abrirModalCuenta(index)}
                              title="Cambiar cuenta contable"
                            >
                              🔍
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => abrirModalCuenta(index)}
                            title="Buscar y asignar cuenta contable manualmente"
                          >
                            Buscar Cuenta
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => removeConcepto(index)}
                      >
                        Eliminar
                      </Button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
              </Table>
            </div>
          ) : (
            <Alert variant="info">
              No hay conceptos agregados. Haz clic en &quot;Agregar Concepto&quot; para comenzar.
            </Alert>
          )}

          <Row className="mt-4">
            <Col md={6}></Col>
            <Col md={6}>
              <Table>
                <tbody>
                  <tr>
                    <td><strong>Subtotal:</strong></td>
                    <td className="text-end">
                      ${parseFloat(formData.subtotal || 0).toLocaleString('es-MX', {
                        minimumFractionDigits: decimalesDisplay,
                        maximumFractionDigits: decimalesDisplay
                      })}
                    </td>
                  </tr>
                  {usaCalculoSatV2 && (
                    <tr>
                      <td><strong>Descuento:</strong></td>
                      <td className="text-end">
                        ${parseFloat(formData.descuento_total || 0).toLocaleString('es-MX', {
                          minimumFractionDigits: decimalesDisplay,
                          maximumFractionDigits: decimalesDisplay
                        })}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      {(() => {
                        const summary = getTaxSummary(conceptos.flatMap((c) => c.impuestos_trasladados || []))
                        const label = summary.labels.length
                          ? `Impuestos (${summary.labels.join(', ')})`
                          : 'Impuestos'
                        return <strong>{label}:</strong>
                      })()}
                    </td>
                    <td className="text-end">
                      ${parseFloat(formData.tax_amount || 0).toLocaleString('es-MX', {
                        minimumFractionDigits: decimalesDisplay,
                        maximumFractionDigits: decimalesDisplay
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      {(() => {
                        const summary = getTaxSummary(conceptos.flatMap((c) => c.retenciones_detalle || []))
                        const label = summary.labels.length
                          ? `Retenciones (${summary.labels.join(', ')})`
                          : 'Retenciones'
                        return <strong>{label}:</strong>
                      })()}
                    </td>
                    <td className="text-end">
                      ${conceptos.reduce((s, c) => s + parseFloat(c.iva_retenido || 0), 0).toLocaleString('es-MX', {
                        minimumFractionDigits: decimalesDisplay,
                        maximumFractionDigits: decimalesDisplay
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td><strong>Total:</strong></td>
                    <td className="text-end">
                      <strong>
                        ${parseFloat(formData.total || 0).toLocaleString('es-MX', {
                          minimumFractionDigits: decimalesDisplay,
                          maximumFractionDigits: decimalesDisplay
                        })}
                      </strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (invoice ? 'Actualizar' : 'Crear')}
        </Button>
      </div>

      <Modal show={showTaxModal} onHide={() => setShowTaxModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{taxModalType === 'traslado' ? 'Impuestos del concepto' : 'Retenciones del concepto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="light">
            Base (importe): <strong>${parseFloat(conceptos[taxConceptIndex]?.importe || 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}</strong><br />
            Formula: <strong>Base (importe) x Tasa (%) = Importe {taxModalType === 'traslado' ? 'impuesto' : 'retención'}</strong>
            {taxModalType === 'traslado' && (
              <>
                <br />
                <small className="text-muted">
                  Tasa 0% y Exento no son lo mismo: use TipoFactor <strong>Tasa</strong> con 0% o <strong>Exento</strong> según el caso SAT.
                </small>
              </>
            )}
            {taxModalType === 'retencion' && permiteRetenciones && (
              <>
                <br />
                <small className="text-muted">
                  Retenciones habilitadas (emisor PF nacional → receptor PM nacional). Capture la tasa aplicable.
                </small>
              </>
            )}
          </Alert>
          <div className="d-flex justify-content-between mb-2">
            <Button size="sm" variant="outline-primary" onClick={addTaxDraftRow}>+ Agregar</Button>
            <div>
              Total: <strong>${taxDraftRows.reduce((s, r) => {
                if ((r.tipo_factor || 'Tasa') === 'Exento') return s
                if (r.importe_ajustado) return s + (parseFloat(r.importe) || 0)
                const base = parseFloat(conceptos[taxConceptIndex]?.importe || 0) || 0
                const tasa = parseFloat(r.tasa || 0) || 0
                return s + roundMoneyN(base * (tasa / 100), decimalesInput)
              }, 0).toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}</strong>
            </div>
          </div>
          <Table size="sm" responsive>
            <thead>
              <tr>
                <th>Impuesto</th>
                {taxModalType === 'traslado' && <th>TipoFactor</th>}
                <th>Tasa %</th>
                <th>Importe</th>
                {taxModalType === 'retencion' && receptorPermiteAjusteRetencion && (
                  <th title="Fijar el importe al centavo que exige el receptor">Ajustar</th>
                )}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {taxDraftRows.map((row, idx) => {
                const allowed = IMPUESTO_CATALOGO.filter((i) => (taxModalType === 'traslado' ? i.traslado : i.retencion))
                const base = parseFloat(conceptos[taxConceptIndex]?.importe || 0) || 0
                const tipoFactor = row.tipo_factor || 'Tasa'
                const tasa = tipoFactor === 'Exento' ? 0 : (parseFloat(row.tasa || 0) || 0)
                const calcImporte = tipoFactor === 'Exento' ? 0 : roundMoneyN(base * (tasa / 100), decimalesInput)
                return (
                  <tr key={`tax-row-${idx}`}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={tipoFactor === 'Exento' ? '002' : (row.impuesto || '')}
                        onChange={(e) => updateTaxDraftRow(idx, 'impuesto', e.target.value)}
                        disabled={tipoFactor === 'Exento'}
                      >
                        <option value="">Seleccione</option>
                        {allowed.map((opt) => (
                          <option key={opt.codigo} value={opt.codigo}>{opt.codigo} {opt.etiqueta}</option>
                        ))}
                      </Form.Select>
                    </td>
                    {taxModalType === 'traslado' && (
                      <td>
                        <Form.Select
                          size="sm"
                          value={tipoFactor}
                          onChange={(e) => updateTaxDraftRow(idx, 'tipo_factor', e.target.value)}
                        >
                          {TIPO_FACTOR_SAT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </Form.Select>
                      </td>
                    )}
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.000001"
                        value={tipoFactor === 'Exento' ? 0 : (row.tasa ?? '')}
                        onChange={(e) => updateTaxDraftRow(idx, 'tasa', e.target.value)}
                        disabled={tipoFactor === 'Exento'}
                      />
                    </td>
                    <td>
                      {row.importe_ajustado ? (
                        <>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="0.01"
                            value={row.importe ?? ''}
                            isInvalid={importeFueraDeRango(base, tasa, row.importe)}
                            onChange={(e) => updateTaxDraftRow(idx, 'importe', e.target.value)}
                          />
                          <Form.Text
                            className={importeFueraDeRango(base, tasa, row.importe) ? 'text-danger' : 'text-muted'}
                          >
                            {mensajeFueraDeRango(base, tasa)}
                          </Form.Text>
                        </>
                      ) : (
                        `$${calcImporte.toLocaleString('es-MX', { minimumFractionDigits: decimalesDisplay, maximumFractionDigits: decimalesDisplay })}`
                      )}
                    </td>
                    {taxModalType === 'retencion' && receptorPermiteAjusteRetencion && (
                      <td className="text-center align-middle">
                        <Form.Check
                          type="checkbox"
                          checked={Boolean(row.importe_ajustado)}
                          title="Fijar el importe manualmente dentro del rango que el SAT admite"
                          onChange={(e) => {
                            const activar = e.target.checked
                            updateTaxDraftRow(idx, 'importe_ajustado', activar)
                            if (activar) {
                              // Arranca del calculado: el contador solo mueve el centavo.
                              updateTaxDraftRow(idx, 'importe', calcImporte.toFixed(2))
                            }
                          }}
                        />
                      </td>
                    )}
                    <td>
                      <Button variant="outline-danger" size="sm" onClick={() => removeTaxDraftRow(idx)}>Quitar</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaxModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={saveTaxDraftRows}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de búsqueda de claves SAT */}
      <Modal
        show={mostrarModalClaveSAT}
        onHide={cerrarModalClaveSAT}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Clave SAT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Buscar por clave o descripción</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Escribe para buscar (mínimo 2 caracteres)..."
                value={busquedaModalClave}
                onChange={(e) => {
                  setBusquedaModalClave(e.target.value)
                  buscarClavesModal(e.target.value)
                }}
                autoFocus
              />
              {buscandoModalClave && (
                <InputGroup.Text>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Busca por clave de producto/servicio o por descripción
            </Form.Text>
          </Form.Group>

          {resultadosModalClave.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '150px' }}>Clave</th>
                    <th>Descripción</th>
                    <th style={{ width: '100px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosModalClave.map((resultado, idx) => (
                    <tr key={idx}>
                      <td><strong>{resultado.clave}</strong></td>
                      <td>
                        <div style={{ fontSize: '0.9rem' }}>
                          {resultado.descripcion}
                        </div>
                        {resultado.version && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Versión: {resultado.version}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => seleccionarClaveSAT(resultado)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : busquedaModalClave.trim().length >= 2 && !buscandoModalClave ? (
            <Alert variant="info" className="mb-0">
              No se encontraron resultados para &quot;{busquedaModalClave}&quot;
            </Alert>
          ) : busquedaModalClave.trim().length < 2 ? (
            <Alert variant="secondary" className="mb-0">
              Escribe al menos 2 caracteres para comenzar la búsqueda
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalClaveSAT}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de búsqueda de cuentas contables */}
      <Modal
        show={mostrarModalCuenta}
        onHide={cerrarModalCuenta}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar Cuenta Contable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Buscar por código o nombre</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Escribe para buscar (mínimo 2 caracteres)..."
                value={busquedaModalCuenta}
                onChange={(e) => {
                  setBusquedaModalCuenta(e.target.value)
                  buscarCuentasModal(e.target.value)
                }}
                autoFocus
              />
              {buscandoModalCuenta && (
                <InputGroup.Text>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </InputGroup.Text>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Busca por código de cuenta o por nombre
            </Form.Text>
          </Form.Group>

          {resultadosModalCuenta.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: '150px' }}>Código</th>
                    <th>Nombre</th>
                    <th style={{ width: '120px' }}>Tipo</th>
                    <th style={{ width: '100px' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {resultadosModalCuenta.map((cuenta) => (
                    <tr key={cuenta.id}>
                      <td><strong>{cuenta.code}</strong></td>
                      <td>
                        <div style={{ fontSize: '0.9rem' }}>
                          {cuenta.name}
                        </div>
                        {cuenta.account_type && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {cuenta.account_type}
                          </div>
                        )}
                      </td>
                      <td>
                        {cuenta.account_type && (
                          <Badge bg="info" style={{ fontSize: '0.75rem' }}>
                            {cuenta.account_type}
                          </Badge>
                        )}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => seleccionarCuenta(cuenta)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : busquedaModalCuenta.trim().length >= 2 && !buscandoModalCuenta ? (
            <Alert variant="info" className="mb-0">
              No se encontraron resultados para &quot;{busquedaModalCuenta}&quot;
            </Alert>
          ) : busquedaModalCuenta.trim().length < 2 ? (
            <Alert variant="secondary" className="mb-0">
              Escribe al menos 2 caracteres para comenzar la búsqueda
            </Alert>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalCuenta}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: conceptos con mapeo disponible sin aplicar */}
      <Modal show={mostrarModalMapeoDisponible} onHide={() => { setMostrarModalMapeoDisponible(false); setPendingSubmitData(null); setConceptosConMapeoDisponible([]) }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Mapeos de cuenta disponibles</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Los conceptos con Clave SAT <strong>{conceptosConMapeoDisponible.map(c => c.clave_sat).join(', ')}</strong> tienen
            mapeos de cuenta disponibles para este cliente.
          </p>
          <p>¿Desea aplicar el mapeo automático o continuar sin aplicar?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleContinuarSinMapeo}>
            Continuar sin aplicar
          </Button>
          <Button variant="primary" onClick={handleAplicarMapeoYEnviar}>
            Aplicar mapeo automático
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  )
}

export default InvoiceForm

